import firebase, { database } from 'firebase/app'
import 'firebase/firestore'
import 'firebase/database'
import _ from 'lodash'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { globalfunc } from '../globalfunc.js'
import axios from 'axios'
import { getByUser, getCompany,getTeamByCompany,getUserAll } from './core/cacheService'

const apiUrl = process.env.VUE_APP_API_URL

const ucfirst = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
const isEmptyObject = (value: object) => {
  return Object.keys(value).length === 0
}

const normalizeImportString = (value: any) => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

const normalizeImportEmail = (value: any) => {
  return normalizeImportString(value).toLowerCase().replace(/\s+/g, '')
}

const normalizeImportHeader = (value: any) => {
  return normalizeImportString(value)
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

const findImportHeaderIndex = (header: any[], aliases: string[], fallback = -1) => {
  const normalizedHeader = header.map(normalizeImportHeader)
  const aliasList = aliases.map(normalizeImportHeader)
  const index = normalizedHeader.findIndex((cell: string) => {
    return aliasList.some((alias) => cell.includes(alias))
  })
  return index !== -1 ? index : fallback
}

const isValidImportEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const buildImportName = (firstName: string, lastName: string) => {
  return [normalizeImportString(firstName), normalizeImportString(lastName)]
    .filter(Boolean)
    .join(' ')
}

const normalizeImportLanguage = (value: any, fallback: string) => {
  const normalized = normalizeImportString(value).toLowerCase()
  if (!normalized) return fallback
  if (['th', 'thai', 'ไทย'].indexOf(normalized) !== -1) return 'th'
  if (['en', 'eng', 'english', 'อังกฤษ'].indexOf(normalized) !== -1) return 'en'
  return fallback
}

const normalizeImportCountry = (value: any, fallback: string) => {
  const normalized = normalizeImportString(value)
  if (!normalized) return fallback
  if (normalized.toLowerCase() === 'thailand') return 'TH'
  return normalized
}

const offsetNumberToString = (value: number) => {
  if (isNaN(value) || value < -12 || value > 14) {
    return null
  }

  const sign = value >= 0 ? '+' : '-'
  const absolute = Math.abs(value)
  const hours = Math.floor(absolute)
  const fraction = Number((absolute - hours).toFixed(2))
  const minuteLookup: Record<string, number> = {
    '0': 0,
    '0.25': 15,
    '0.5': 30,
    '0.75': 45,
  }
  const minutes = minuteLookup[String(fraction)]
  if (minutes === undefined) {
    return null
  }

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const normalizeImportTimezone = (input: any, fallback: any) => {
  const fallbackNumber = typeof fallback === 'number' ? fallback : Number(fallback)
  const fallbackOffset = typeof fallback === 'string'
    ? offsetStringToNumber(fallback)
    : fallbackNumber
  const fallbackValue = offsetNumberToString(fallbackOffset)
    || offsetNumberToString(fallbackNumber)
    || '+07:00'
  const raw = normalizeImportString(input)

  if (!raw) {
    return { value: fallbackValue }
  }

  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    if ([0, 15, 30, 45].indexOf(minutes) === -1) {
      return {
        value: fallbackValue,
        error: 'Timezone must use 00, 15, 30, or 45 minute increments.',
      }
    }
    const decimal = sign * (hours + minutes / 60)
    const normalized = offsetNumberToString(decimal)
    if (normalized) {
      return { value: normalized }
    }
    return {
      value: fallbackValue,
      error: 'Timezone must be between UTC-12 and UTC+14.',
    }
  }

  const numeric = Number(raw)
  if (!isNaN(numeric)) {
    const normalized = offsetNumberToString(numeric)
    if (normalized) {
      return { value: normalized }
    }
  }

  return {
    value: fallbackValue,
    error: 'Timezone must be between UTC-12 and UTC+14.',
  }
}

function offsetStringToNumber(value: any) {
  const raw = normalizeImportString(value)
  if (!raw) return 0
  const colonMatch = raw.match(/^([+-]?)(\d{1,2}):(\d{2})$/)
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1
    const hours = parseInt(colonMatch[2], 10)
    const minutes = parseInt(colonMatch[3], 10)
    return sign * (hours + minutes / 60)
  }
  const numeric = Number(raw)
  return isNaN(numeric) ? 0 : numeric
}

const isLeapYear = (year: number) => {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)
}

const isValidDateParts = (year: number, month: number, day: number) => {
  if (!year || month < 1 || month > 12 || day < 1) return false
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

const normalizeImportDate = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let year = 0
  let month = 0
  let day = 0

  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    year = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    const third = parseInt(parts[2], 10)
    year = third
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else {
    return {
      value: '',
      error: 'Date must use YYYY-MM-DD or MM/DD/YYYY.',
    }
  }

  if (!isValidDateParts(year, month, day)) {
    return {
      value: '',
      error: 'Date is invalid.',
    }
  }

  return {
    value: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  }
}

const normalizeImportBirthday = (input: any) => {
  const raw = normalizeImportString(input)
  if (!raw) {
    return { value: '' }
  }

  let month = 0
  let day = 0

  if (/^\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(raw)) {
    const parts = raw.split(/[-/]/)
    const first = parseInt(parts[0], 10)
    const second = parseInt(parts[1], 10)
    if (first > 12 && second <= 12) {
      month = second
      day = first
    } else {
      month = first
      day = second
    }
  } else {
    return {
      value: '',
      error: 'Birthday must use MM-DD. Full dates are accepted, but only month and day are stored.',
    }
  }

  if (!isValidDateParts(2000, month, day)) {
    return {
      value: '',
      error: 'Birthday is invalid.',
    }
  }

  return {
    value: `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  }
}

const normalizeStoredBirthday = (input: any) => {
  const normalized = normalizeImportBirthday(input)
  return normalized.value || normalizeImportString(input)
}

const normalizeStoredWorkdate = (input: any) => {
  const normalized = normalizeImportDate(input)
  return normalized.value || normalizeImportString(input)
}

export default {
  namespaced: true,
  state: {
    selectedSso: {},
    dataSettings: { 'webhook_logs': [] },
    directorySyncSettings: {},
    hasDirectorySyncSettings: false,
    apiKeys: {},
    apiKeyDev: {},
    apiKeyProd: {},
    apiLogs: { page: 1, data: [], results: [], error: false, errorMsg: '' },
    resultsSimulate: [],
    membersSimulate: [],
    membersSimulateAll: [],
    loadingSimulateTable: true,
    inputSearchSimulate: '',
    size_page: 10,
    sortfieldsimulate: 'value',
    sortordersimulate: 'asc',
    pagesimulate: 1,
    resultsView: [],
    membersView: [],
    membersViewAll: [],
    loadingViewTable: true,
    inputSearchView: '',
    sortfieldview: 'value',
    sortorderview: 'asc',
    pageview: 1,
    tokenLength: 30,

    reviewUpdates: [],
    reviewUpdatesAll: [],
    reviewUpdatesTotal: 0,
    reviewUpdatesPage: 1,
    reviewUpdatesByKey: [],
    pageReviewUpdates: 1,
    sortfieldreviewupdates: 'email',
    sortordereviewupdates: 'asc',
    reviewUpdatesLoading: false,
    _reviewUpdates: [],
    _reviewUpdatesAll: [],
    reviewUpdatesIgnorePendingStatus: false, // true for dev mode

    // import members
    importMembersTableDataGroups: [],
    importMembersTableDataGroupsTotal: 0,
    importMembersSearch: "",
    origImportMembersTableDataGroups: [],
    origImportMembersTableDataGroupsTotal: 0,
  },
  mutations: {
    selectedSso(state: any, result: object) {
      state.selectedSso = result
    },
    setApiKeys(state: any, result: any) {
      state.apiKeys = result
    },
    setApiKeyDev(state: any, result: any) {
      state.apiKeyDev = result
    },
    setApiKeyProd(state: any, result: any) {
      state.apiKeyProd = result
    },
    unsetApiKeyDev(state: any, result: any) {
      const apiKeyDev = {
        generate: false,
        token: '',
        mockTaken: [],
        showingStatus: false,
      }
      state.apiKeyDev = apiKeyDev;
    },
    unsetApiKeyProd(state: any, result: any) {
      const apiKeyProd = {
        generate: false,
        token: '',
        mockTaken: [],
        showingStatus: false,
      }
      state.apiKeyProd = apiKeyProd;
    },
    setApiLogs(state: any, result: any) {
      state.apiLogs = result
    },
    setDataIntegrationSettings(state: any, result: any) {
      state.dataSettings = result
    },
    setHasDirectorySyncSettings(state: any, result: any) {
      state.hasDirectorySyncSettings = result
    },
    setDirectorySyncSettings(state: any, result: any) {
      state.directorySyncSettings = result
    },
    settokenTemp(state: any) {
      state.dataSettings.token_temp = globalfunc.generateToken(state.tokenLength)
    },
    setResultsSimulate(state: any, result: any) {
      state.resultsSimulate = result
    },
    setMemberSimulate(state: any, value: any) {
      state.membersSimulate = value
    },
    setMemberSimulateAll(state: any, value: any) {
      state.membersSimulateAll = value
    },
    setLoadingSimulateTable(state: any, result: any) {
      state.loadingSimulateTable = result
    },
    setinputSearchSimulate(state: any, value: any) {
      state.inputSearchSimulate = value.keyword
    },
    setPageSimulate(state: any, result: any) {
      state.pagesimulate = result
    },
    setSortfieldSimulate(state: any, result: any) {
      state.sortfieldsimulate = result
    },
    setSortorderSimulate(state: any, result: any) {
      state.sortordersimulate = result
    },
    setResultsView(state: any, result: any) {
      state.resultsView = result
    },
    setMemberView(state: any, value: any) {
      state.membersView = value
    },
    setMemberViewAll(state: any, value: any) {
      state.membersViewAll = value
    },
    setLoadingViewTable(state: any, result: any) {
      state.loadingViewTable = result
    },
    setinputSearchView(state: any, value: any) {
      state.inputSearchView = value.keyword
    },
    setPageView(state: any, result: any) {
      state.pageview = result
    },
    setSortfieldView(state: any, result: any) {
      state.sortfieldview = result
    },
    setSortorderView(state: any, result: any) {
      state.sortorderview = result
    },
    setReviewUpdates(state: any, result: any) {
      state.reviewUpdates = result
    },
    setReviewUpdatesAll(state: any, result: any) {
      state.reviewUpdatesAll = result
    },
    setReviewUpdatesLoading(state: any, result: any) {
      state.reviewUpdatesLoading = result
    },
    setReviewUpdatesTotal(state: any, result: any) {
      state.reviewUpdatesTotal = result
    },
    reviewUpdatesPage(state: any, result: any) {
      state.reviewUpdatesPage = result
    },
    setReviewUpdatesByKey(state: any, result: any) {
      state.reviewUpdatesByKey = result
    },
    setPageReviewUpdates(state: any, result: any) {
      state.pageReviewUpdates = result
    },
    setSortfieldReviewUpdates(state:any,result:any) {
      state.sortfieldreviewupdates = result
    },
    setSortorderReviewUpdates(state:any,result:any) {
      state.reviewupdates = result
    },
    setImportMembersTableDataGroups(state:any,result:any) {
      state.importMembersTableDataGroups = result
    },
    setImportMembersTableDataGroupsTotal(state:any,result:any) {
      state.importMembersTableDataGroupsTotal = result
    },
    setImportMembersSearch(state:any,result:any) {  
      state.importMembersSearch = result
    },
    setOrigImportMembersTableDataGroups(state:any,result:any) {
      state.origImportMembersTableDataGroups = result
    },
    setOrigImportMembersTableDataGroupsTotal(state:any,result:any) {
      state.origImportMembersTableDataGroupsTotal = result
    },
  },
  actions: {

    async getUserCompany({ commit, rootState, dispatch, state }: any) {
      return await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
    },

    /* sso */

    // _createSsoBySaml()

    // used for unauthenticated story
    async getSelectedSsoByEmail({ commit, rootState, dispatch, state }: any, email: string) {
      let user;
      if(email){
        const emailKey = email.replace(/\./g, '||')
        if (rootState.main.allUser[emailKey]) {
          user = rootState.main.allUser[emailKey];
        } else {
          user = await getByUser(email)//(await firebase.database().ref(`/users/${emailKey}`).once('value')).val();
        }
      }
      let selectedSso = null
      if (user) {
        const company = await getCompany(user.company)//(await firebase.database().ref(`company/${user.company}`).once('value')).val();
        const sso_configuration = company.sso_configuration || {};
        const options = sso_configuration.options || [];
        selectedSso = _.find(options, ['selected', true]);
      }
      return selectedSso
    },

    async createDefaultSsoConfiguration({ commit, rootState, dispatch, state }: any, values: object) {
      const sso_configuration = Object.assign({
        enabled: false,
        options: []
      }, values)

      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })

      return sso_configuration
    },

    async getSsoConfiguration({ commit, rootState, dispatch, state }: any) {
      let company = await dispatch('getUserCompany')
      let sso_configuration = company.sso_configuration || {}
      if (isEmptyObject(sso_configuration)) {
        sso_configuration = await dispatch('createDefaultSsoConfiguration', { enabled: true })
      }
      return sso_configuration
    },

    async saveSso({ commit, rootState, dispatch, state }: any, selectedSso: any) {
      let sso_configuration = await dispatch('getSsoConfiguration')
      if (!sso_configuration.options) sso_configuration.options = []

      let found = false
      for (let i in sso_configuration.options) {
        sso_configuration.options[i].selected = false
        if (sso_configuration.options[i].ssoProviderId == selectedSso.ssoProviderId) {
          sso_configuration.options[i] = selectedSso
          found = true
        }
      }

      if (!found) {
        sso_configuration.options.push(selectedSso)
      }

      // Create or Update (Inconsistency 2 way apis)
      // console.log(found, 'found')
      if (found) {

        const asyncFunction = (resolve, reject) => {
          axios.put(`${apiUrl}/identity-platform/update-config-provider/${selectedSso.ssoProviderId}`, {
            "displayName": selectedSso.connectionName,
            "enabled": selectedSso.enabled,
            "idpEntityId": selectedSso.idpEntityId,
            "ssoURL": selectedSso.ssoUrl,
            "x509Certificates": [selectedSso.certificate],
            "rpEntityId": selectedSso.spEntityId,
            "callbackURL": selectedSso.acsUrl
          })
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;

      } else {

        const asyncFunction = (resolve, reject) => {
          axios.post(`${apiUrl}/identity-platform/create-config-provider`, {
            "displayName": selectedSso.connectionName,
            "enabled": selectedSso.enabled,
            "providerId": selectedSso.ssoProviderId,
            "idpEntityId": selectedSso.idpEntityId,
            "ssoURL": selectedSso.ssoUrl,
            "x509Certificates": [selectedSso.certificate],
            "rpEntityId": selectedSso.spEntityId,
            "callbackURL": selectedSso.acsUrl
          })
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;
      }


      // @TODO update only option
      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })
    },

    async deleteSso({ commit, rootState, dispatch, state }: any, selectedSso: any) {
      let sso_configuration = await dispatch('getSsoConfiguration')
      if (!sso_configuration.options) sso_configuration.options = []

      let found = false
      let index = ''
      for (let i in sso_configuration.options) {
        if (sso_configuration.options[i].ssoProviderId == selectedSso.ssoProviderId) {
          sso_configuration.options[i] = selectedSso
          found = true
          index = i
        }
      }

      if (found) {
        const ssoProviderId = sso_configuration.options[index].ssoProviderId
        delete sso_configuration.options[index]

        const asyncFunction = (resolve, reject) => {
          axios.delete(`${apiUrl}/identity-platform/delete-config-provider/${ssoProviderId}`)
            .then(function (response) {
              // console.log(response, 'response');
              resolve(response)
            })
            .catch(function (error) {
              // console.log(error, 'error');
              reject(error)
            });
        }

        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve, reject)
        })

        await fn;
      }

      // @TODO update only option
      await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
        "sso_configuration": sso_configuration,
        "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
      })
    },

    /* api key */

    async getApiKeys({ commit, rootState, dispatch, state }: any) {
      // @todo use common function
      let company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
      const emptyApiKeyObj = {
        generate: false,
        token: "",
        mockTaken: new Array,
        showingStatus: false,
      }
      let apiKeys = company.api_keys || {};

      const auto_create_api_key_dev = company.auto_create_api_key_dev || false;

      let apiKeyDev = Object.assign({}, emptyApiKeyObj);
      let apiKeyProd = Object.assign({}, emptyApiKeyObj);

      const assignApiKeyValue = (targetObj, valueObj) => {
        if (valueObj) {
          targetObj.generate = true;
          targetObj.token = valueObj.api_key;

          let token = new Array;
          for (let i: number = 0; i < targetObj.token.length; i++) {
            token.push(i);
          }
          targetObj.mockTaken = token;
        }
      }

      if (!auto_create_api_key_dev && !apiKeys.dev) {
        await dispatch('createApiKey', 'dev');

        // reload new data for company and apiKeys
        company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
        apiKeys = company.api_keys || {};

        // @todo update company flags
        await firebase.database().ref(`company/${rootState.authen.user.company}`).update({
          "auto_create_api_key_dev": true,
          "updated": format(new Date(), "YYYY-MM-DD HH:mm:ss")
        })
      }

      if (apiKeys.dev) {
        assignApiKeyValue(apiKeyDev, apiKeys.dev);
      }

      if (apiKeys.prod) {
        assignApiKeyValue(apiKeyProd, apiKeys.prod);
      }

      commit('setApiKeys', apiKeys)
      commit('setApiKeyDev', apiKeyDev);
      commit('setApiKeyProd', apiKeyProd);
    },

    async createApiKey({ commit, rootState, dispatch, state }: any, stageName: string) {
      const asyncFunction = (resolve) => {
        // console.log(stageName, 'stageName');
        axios.post(`${apiUrl}/apigateway/create-api-key`, {
          "companyId": rootState.authen.user.company,
          "usagePlanId": "3shwv6",
          "restApiId": "ln1buvf68h",
          "stageName": stageName
        })
          .then(function (response) {
            // console.log(response, 'response');
            const data = response.data;
            if (data.statusCode != 500) {
              const apiKeyObj = {
                generate: true,
                token: data.value,
                mockTaken: [],
                showingStatus: false,
              }
              commit(`setApiKey${ucfirst(stageName)}`, apiKeyObj);
              resolve(apiKeyObj);
            }
          })
          .catch(function (error) {
            console.log(error, 'error');
          });
      }

      const fn = new Promise((resolve, reject) => {
        asyncFunction(resolve)
      })

      await fn;
    },

    async removeApiKey({ commit, rootState, dispatch, state }: any, stageName: string) {
      const company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
      try {
        const apiKey = company.api_keys[stageName].api_key
        const asyncFunction = (resolve) => {
          axios.post(`${apiUrl}/apigateway/delete-api-key`, {
            "apiKey": apiKey
          })
            .then(function (response) {
              commit(`unsetApiKey${ucfirst(stageName)}`)
              resolve();
            })
            .catch(function (error) {
              console.log(error, 'error');
              resolve();
            })
        }
        const fn = new Promise((resolve, reject) => {
          asyncFunction(resolve)
        })
        await fn;
      } catch (e) {
        console.log(e);
      }
    },

    async getApiLogs({ commit, rootState, dispatch, state }: any) {
      let results: any = { page: 1, data: [], results: [] };

      const companyId = rootState.authen.user.company;

      let dateNow = new Date();
      dateNow.setHours(dateNow.getHours() + 7);
      let endDate = dateNow.getTime();

      let diffDay: number;
      diffDay = dateNow.getDate() - 7;
      dateNow.setDate(diffDay);

      let startDate = dateNow.getTime();

      // @see https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/CloudWatchLogs.html#filterLogEvents-property
      axios.post(`${apiUrl}/logs/filter-log-events`, {
        "logGroupName": process.env.VUE_APP_API_LOG_GROUP,
        "endTime": endDate,
        //"limit": 10000,
        "logStreamNamePrefix": companyId + "||",
        "startTime": startDate
        //"interleaved": true
      })
        .then(function (response) {
          try {
            const events = response.data.events || [];

            const monthNames = ["January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];

            const langMapping = {
              "dev": "Developement",
              "prod": "Production",
              "getMedalById": "Happily Medal: Get Medal",
              "getRecognitionsForMember": "Happily Recognition - Get Recognitions for Member",
              "createRecognition": "Happily Recognition - Create Recognition"
            }

            const pad = (num, size) => {
                var s = "00" + num;
                return s.substring(s.length-size);
            }

            events.forEach(event => {
              const rawData = JSON.parse(event.message);

              // @fixed 13 digits timestamp with milliseconds value
              const date = new Date(event.ingestionTime);
              const YYYY = date.getFullYear();
              const MMMM = monthNames[date.getMonth()];
              const d = date.getDate();
              const h = ("0" + (date.getHours() >= 12 ? (date.getHours() - 12) : date.getHours())).slice(-2);
              const mm = pad(date.getMinutes(), 2);
              const A = date.getHours() >= 12 ? "PM" : "AM";

              const displayedDate = `${MMMM} ${d}, ${YYYY}`
              const displayedTime = `${h}:${mm} ${A}`

              const data = {
                // date: format(parse(event.ingestionTime),'MMMM d, YYYY'),
                // time: format(parse(event.ingestionTime),'h:mm A'),
                eventId: event.eventId,
                logStreamName: event.logStreamName,
                timestamp: event.timestamp,
                date: displayedDate,
                time: displayedTime,
                operation: langMapping[rawData.operation],
                status: rawData.status ? 'Success' : 'Failed',
                deployment: langMapping[rawData.deployment]
              };

              results.data.push(data)
            });

            commit('setApiLogs', results)
            dispatch('pageCurrentLogChange', 1)
          } catch (e) {
            console.log(e, 'e');
          }
        })
        .catch(function (error) {
          console.log(error, 'error');
          const apiLogs = { page: 1, data: [], results: [], error: true, errorMsg: error.message }
          commit('setApiLogs', apiLogs)
        });

    },

    pageCurrentLogChange({ commit, rootState, dispatch, state }: any, value: number) {
      let sizePage = 10;
      let start_row = (value - 1) * sizePage
      let end_row = ((value - 1) * sizePage) + sizePage

      state.apiLogs.page = value;
      state.apiLogs.results = state.apiLogs.data.slice(start_row, end_row)
      commit('setApiLogs', state.apiLogs)
    },

    async getDataIntregrationSetting({ commit, rootState, dispatch, state }: any, user: any) {
      let results: any
      let dateNow = new Date()

      await firebase.database().ref(`integration_settings/${rootState.authen.user.company}`).once('value', (snapshot) => {
        if (snapshot.val()) {
          results = snapshot.val()
        } else {
          let objData = {
            webhook: 'https://integrations-bv43tjyzrq-de.a.run.app/users',
            token: globalfunc.generateToken(state.tokenLength),
            sync_type: 'normal',
            live: false,
            abbreviate_team_name: false,
            updated: format(dateNow, 'YYYY-MM-DD HH:mm'),
            created: format(dateNow, 'YYYY-MM-DD HH:mm'),

          }
          firebase.database().ref(`/integration_settings`).child(rootState.authen.user.company).set(objData)
          results = objData
        }
      });
      results.webhook_logs = new Array()
      await firebase.database().ref('integration_request_logs')
        .orderByChild('comp_id')
        .equalTo(rootState.authen.user.company)
        .once('value', snapshot => {
          snapshot.forEach((doc): any => {
            let data = doc.val()
            data.parse_date = format(parse(data.batch_ct), 'MMMM d, YYYY')
            data.parse_time = format(parse(data.batch_ct), 'h:mm A')
            data.content = JSON.stringify(data.request_data)
            results.webhook_logs.push(data)
          });
        });

      results.token_temp = results.token
      // json_encode
      commit('setDataIntegrationSettings', results)

      // @todo connect realtime database
      commit('setHasDirectorySyncSettings', false);
      commit('setDirectorySyncSettings', {
        'endpoint': 'https://api.workos.com/scim/v2.0/UNxMntQuAss69Pns/?aadOptscim062020',
        'token': 'se_WWwndlq80MmrxLfrz8bbOBfEN',
        'sync_type': 'staged',
        'live': true,
      });
    },
    saveChanges({ commit, rootState, dispatch, state }: any, obj: any) {
      let dateNow = new Date()
      let form = obj.form
      firebase.database().ref(`/integration_settings/${rootState.authen.user.company}`).update({
        token: form.token_temp ? form.token_temp : form.token,
        sync_type: form.sync_type,
        live: form.live,
        abbreviate_team_name: form.abbreviate_team_name,
        updated: format(dateNow, 'YYYY-MM-DD HH:mm')
      }).then(() => {
        dispatch('authen/addLogs', {
          company: rootState.authen.user.company,
          createdate: format(dateNow, 'YYYY-MM-DD HH:mm:ss'),
          id: rootState.authen.user.company,
          table: 'integration_settings',
          status: 'updateIntegrationSetting',
          user: rootState.authen.user.email
        }, { root: true })
      }).then(() => {
        obj.messageLoading.close();
        obj.thisvue.$message({
          type: 'success',
          message: 'Your settings have been updated',
        });
      })
    },
    async getSimulateData({ commit, rootState, dispatch, state }: any, obj: any) {
      commit('setLoadingSimulateTable', true)
      let allUser: Array<any> = [];
      let arrUser: Array<any> = [];
      state.membersSimulate = []
      state.membersSimulate = []
      let start_date = format(parse(obj.batch_ct), 'MMM d, YYYY')
      let start_date_format = format(parse(obj.batch_ct), 'YYYY-MM-DD')
      const allUserKey = await getUserAll(obj.comp_id)//rootState.main.allUser //(await firebase.database().ref('users').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      const team = await getTeamByCompany(obj.comp_id)//(await firebase.database().ref('team').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      for(const arrData in allUserKey){
        const u =  allUserKey[arrData]
        u.key = arrData
        u.value = allUserKey[arrData].firstname + " " + allUserKey[arrData].lastname
        let k_boss = allUserKey[arrData].boss.replace(/\./g, "||")
        if (k_boss && allUserKey[k_boss] != undefined) {
          u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
        }
        if (u.team) u.team_name = (team[u.team] != undefined) ? team[u.team]["name"] : ''
        if (u.profilepic_small) {
          u.pic = u.profilepic_small;
        } else if (u.profilepic_crop) {
          u.pic = u.profilepic_crop;
        } else if (u.profilepic) {
          u.pic = u.profilepic;
        } else {
          u.pic = "";
        }
        u.change_type = 'None'
        allUser[u.key] = u
      }
      await firebase.database().ref().child('users_preview').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
        snapshot.forEach((arrData): any => {
          const u = arrData.val()
          if (u.company == obj.comp_id) {
            u.key = arrData.key
            u.value = arrData.val().firstname + " " + arrData.val().lastname
            let k_boss = arrData.val().boss.replace(/\./g, "||")
            if (k_boss && allUserKey[k_boss] != undefined) {
              u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
            }
            u.team_name = (u.team != undefined) ? u.team : ''
            u.pic = (allUser[u.key] != undefined) ? allUser[u.key].pic : ''
            if (u.action_type == 'add') {
              u.change_type = 'New Member'
            } else if (u.action_type == 'update') {
              u.change_type = 'Profile Updated'
            } else if (u.action_type == 'delete') {
              u.change_type = 'Archived'
            }
            u.start_date = start_date
            u.start_date_format = start_date_format
            allUser[u.key] = u
          }
        });
        for (const x in allUser) {
          arrUser.push(allUser[x])
        }
        var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
        commit('setResultsSimulate', arrUser)
        commit('setMemberSimulate', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
        commit('setMemberSimulateAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
        commit('setLoadingSimulateTable', false)
        if (state.inputSearchSimulate) {
          dispatch('searchMembersSimulate', state.inputSearchSimulate)
        }
      });
      // await firebase.database().ref().child('users').orderByChild('company').equalTo(`${obj.comp_id}`).once('value', async snapshot => {
      //   snapshot.forEach((arrData): any => {
      //     const u = arrData.val()
      //     u.key = arrData.key
      //     u.value = arrData.val().firstname + " " + arrData.val().lastname
      //     let k_boss = arrData.val().boss.replace(/\./g, "||")
      //     if (k_boss && allUserKey[k_boss] != undefined) {
      //       u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
      //     }
      //     if (u.team) u.team_name = (team[u.team] != undefined) ? team[u.team]["name"] : ''
      //     if (u.profilepic_small) {
      //       u.pic = u.profilepic_small;
      //     } else if (u.profilepic_crop) {
      //       u.pic = u.profilepic_crop;
      //     } else if (u.profilepic) {
      //       u.pic = u.profilepic;
      //     } else {
      //       u.pic = "";
      //     }
      //     u.change_type = 'None'
      //     allUser[u.key] = u
      //   });
      //   await firebase.database().ref().child('users_preview').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
      //     snapshot.forEach((arrData): any => {
      //       const u = arrData.val()
      //       if (u.company == obj.comp_id) {
      //         u.key = arrData.key
      //         u.value = arrData.val().firstname + " " + arrData.val().lastname
      //         let k_boss = arrData.val().boss.replace(/\./g, "||")
      //         if (k_boss && allUserKey[k_boss] != undefined) {
      //           u.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
      //         }
      //         u.team_name = (u.team != undefined) ? u.team : ''
      //         u.pic = (allUser[u.key] != undefined) ? allUser[u.key].pic : ''
      //         if (u.action_type == 'add') {
      //           u.change_type = 'New Member'
      //         } else if (u.action_type == 'update') {
      //           u.change_type = 'Profile Updated'
      //         } else if (u.action_type == 'delete') {
      //           u.change_type = 'Archived'
      //         }
      //         u.start_date = start_date
      //         u.start_date_format = start_date_format
      //         allUser[u.key] = u
      //       }
      //     });
      //     for (const x in allUser) {
      //       arrUser.push(allUser[x])
      //     }
      //     var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
      //     commit('setResultsSimulate', arrUser)
      //     commit('setMemberSimulate', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
      //     commit('setMemberSimulateAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
      //     commit('setLoadingSimulateTable', false)
      //     if (state.inputSearchSimulate) {
      //       dispatch('searchMembersSimulate', state.inputSearchSimulate)
      //     }
      //   });
      // }).catch(error => {
      //   console.log(error)
      // });
    },
    searchMembersSimulate({ commit, state }: any, value: string) {
      let field = state.sortfieldsimulate
      let order = state.sortordersimulate
      commit('setPageSimulate', 1)
      const member: Array<any> = [];
      member.push(state.membersSimulateAll)

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc')
        order = 'asc'
      else
        order = 'desc'

      if (value.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(value, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          let response_sort = _.orderBy(response, [field], [order]);
          commit('setResultsSimulate', response_sort)
          commit('setMemberSimulate', response_sort.slice(0, state.size_page))
        }
      } else if (value.length == 0) {
        let response_sort = _.orderBy(member[0], [field], [order]);
        commit('setResultsSimulate', response_sort)
        commit('setMemberSimulate', response_sort.slice(0, state.size_page))
      }
    },
    sortMemberSimulate({ commit, state }: any, value: any) {
      var member: Array<any> = [];
      member.push(state.membersSimulateAll)
      if (state.inputSearchSimulate.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchSimulate, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }

      var size = state.size_page
      var page = state.pagesimulate
      var field = value[0]
      var order = value[1]
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc') {
        order = 'asc'
      } else {
        order = 'desc'
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldSimulate', field)
      commit('setSortorderSimulate', order)
      commit('setMemberSimulate', _users.slice(start_row, end_row))
    },
    simulateCurrentChange({ commit, state }: any, page: any) {
      var size = state.size_page
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size
      let member: Array<any> = [];
      member.push(state.membersSimulateAll)
      if (state.inputSearchSimulate.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchSimulate, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member[0] = response
        }
      }

      var field = state.sortfieldsimulate
      var _users = _.orderBy(member[0], [field], [state.sortordersimulate]);
      commit('setPageSimulate', page)
      commit('setMemberSimulate', _users.slice(start_row, end_row))
    },
    async getViewData({ commit, rootState, dispatch, state }: any, obj: any) {
      commit('setLoadingViewTable', true)
      let allUser: Array<any> = [];
      let arrUser: Array<any> = [];
      state.membersView = []
      state.membersView = []
      let start_date = format(parse(obj.batch_ct), 'MMM d, YYYY')
      let start_date_format = format(parse(obj.batch_ct), 'YYYY-MM-DD')
      const allUserKey = await getUserAll(obj.comp_id)//rootState.main.allUser//(await firebase.database().ref('users').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      const team =await getTeamByCompany(obj.comp_id)// (await firebase.database().ref('team').orderByChild("company").equalTo(obj.comp_id).once('value')).val()
      await firebase.database().ref().child('integration_changelogs').orderByChild('batch_ct').equalTo(`${obj.batch_ct}`).once('value', snapshot => {
        snapshot.forEach((arrData): any => {
          const u = arrData.val()
          if (u.comp_id == obj.comp_id) {
            if (u.change_logs.length > 0) {
              _.forEach(u.change_logs, (item, key) => {
                let dataUser = item
                dataUser.key = dataUser.email.replace(/\./g, "||")
                dataUser.value = dataUser.firstname + " " + dataUser.lastname
                let k_boss = dataUser.boss.replace(/\./g, "||")
                if (k_boss && allUserKey[k_boss] != undefined) {
                  dataUser.boss_name = allUserKey[k_boss]["firstname"] + " " + allUserKey[k_boss]["lastname"]
                }
                dataUser.team_name = (dataUser.team != undefined) ? dataUser.team : ''
                if (allUserKey[dataUser.key] != undefined) {
                  if (allUserKey[dataUser.key].profilepic_small) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic_small;
                  } else if (allUserKey[dataUser.key].profilepic_crop) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic_crop;
                  } else if (allUserKey[dataUser.key].profilepic) {
                    dataUser.pic = allUserKey[dataUser.key].profilepic;
                  } else {
                    dataUser.pic = "";
                  }
                } else {
                  dataUser.pic = "";
                }

                if (dataUser.action_type == 'add') {
                  dataUser.change_type = 'New Member'
                } else if (dataUser.action_type == 'update') {
                  dataUser.change_type = 'Profile Updated'
                } else if (dataUser.action_type == 'delete') {
                  dataUser.change_type = 'Archived'
                }
                dataUser.start_date = start_date
                dataUser.start_date_format = start_date_format
                arrUser.push(dataUser)
              });
            }
          }
        });
        var _users = _.sortBy(arrUser, [function (o: any) { return o.firstname; }])
        commit('setResultsView', arrUser)
        commit('setMemberView', _.sortBy(_users.slice(0, state.size_page), [function (o: any) { return o.firstname; }]))
        commit('setMemberViewAll', _.sortBy(arrUser, [function (o: any) { return o.firstname; }]))
        commit('setLoadingViewTable', false)
        if (state.inputSearchView) {
          dispatch('searchMembersView', state.inputSearchview)
        }
      });
    },
    searchMembersView({ commit, state }: any, value: string) {
      let field = state.sortfieldview
      let order = state.sortorderview
      commit('setPageView', 1)
      const member: Array<any> = [];
      member.push(state.membersViewAll)

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc')
        order = 'asc'
      else
        order = 'desc'

      if (value.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(value, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          let response_sort = _.orderBy(response, [field], [order]);
          commit('setResultsView', response_sort)
          commit('setMemberView', response_sort.slice(0, state.size_page))
        }
      } else if (value.length == 0) {
        let response_sort = _.orderBy(member[0], [field], [order]);
        commit('setResultsView', response_sort)
        commit('setMemberView', response_sort.slice(0, state.size_page))
      }
    },
    sortMemberView({ commit, state }: any, value: any) {
      var member: Array<any> = [];
      member.push(state.membersViewAll)
      if (state.inputSearchView.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchView, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }

      var size = state.size_page
      var page = state.pageview
      var field = value[0]
      var order = value[1]
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size

      if (field == 'name')
        field = 'value'

      if (order == 'ascending' || order == 'asc') {
        order = 'asc'
      } else {
        order = 'desc'
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldView', field)
      commit('setSortorderView', order)
      commit('setMemberView', _users.slice(start_row, end_row))
    },
    viewCurrentChange({ commit, state }: any, page: any) {
      var size = state.size_page
      var start_row = (page - 1) * size
      var end_row = ((page - 1) * size) + size
      let member: Array<any> = [];
      member.push(state.membersViewAll)
      if (state.inputSearchView.length > 2) {
        if (member.length > 0) {
          var re = new RegExp(state.inputSearchView, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            }
          });
          member[0] = response
        }
      }

      var field = state.sortfieldview
      var _users = _.orderBy(member[0], [field], [state.sortorderview]);
      commit('setPageView', page)
      commit('setMemberView', _users.slice(start_row, end_row))
    },

    async getReviewUpdatesTotal({commit,rootState,dispatch,state}:any, user:any){
      let query = firebase.firestore()
        .collection('integrations')
        .where('company','==', user.company);
      
      if (!state.reviewUpdatesIgnorePendingStatus) {
        query = query.where('status','==', 'pending')
      }

      await query.get()
        .then((querySnapshot)=> {
          commit('setReviewUpdatesTotal', querySnapshot.size);
        });
    },

    beginBulkUpdates({ commit, rootState, dispatch, state }: any, keys: string[]) {
      commit("setReviewUpdatesLoading", true);
      state._reviewUpdates = state.reviewUpdates;
      state._reviewUpdatesAll = state.reviewUpdatesAll;
      commit("setReviewUpdates", []);
      commit("setReviewUpdatesAll", []);
    },

    endBulkUpdates({ commit, rootState, dispatch, state }: any, keys: []) {
      commit("setReviewUpdatesLoading", false);

      const reviewUpdates = _.cloneDeep(state._reviewUpdates);
      const reviewUpdatesAll = _.cloneDeep(state._reviewUpdatesAll);

      for (const i in keys) {
        _.remove(reviewUpdates, {key: keys[i]});
        _.remove(reviewUpdatesAll, {key: keys[i]});
      }

      commit("setReviewUpdates", reviewUpdates);
      commit("setReviewUpdatesAll", reviewUpdatesAll);

      // @todo pagination
      // const items = _.cloneDeep(state.reviewUpdates);
      const items = state.reviewUpdates;
      const size = state.size_page
      const page = state.pageReviewUpdates
      const start_row = (page-1) * size
      const end_row = ((page-1) * size)+size

      const pageTotal = reviewUpdatesAll.length;

      const newPage = Math.ceil((pageTotal -1) / size)

      // update current items in table view
      const realItems = reviewUpdatesAll.slice(start_row,end_row)
      if (realItems.length > items.length) {
        items.push(realItems[items.length -1]);
      }

      // update pagination
      // state.reviewUpdates = items;
      state.reviewUpdates = _.clone(items); // prevent non-update on view
      if (newPage < page) {
        commit('setPageReviewUpdates', newPage)
      }

      // update page total
      commit('setReviewUpdatesTotal', pageTotal);
    },

    async updateReviewUpdates({commit,rootState,dispatch,state}:any, item:any){
      // console.log('updateReviewUpdates()');
      // console.log(item, "item");

      // firebase.firestore().collection('townhall').doc(topic_id).update(topic).then(() => {
      await firebase.firestore().collection(`integrations`).doc(item.key).get().then(async (doc) => {    
        await firebase.firestore().collection('integrations').doc(item.key).update({ status: item.status }).then(() => {
          // console.log('remove item: ' + item.key);

          /*

          _.remove(state.reviewUpdates, {key: item.key});
          _.remove(state.reviewUpdatesAll, {key: item.key});

          // const items = _.cloneDeep(state.reviewUpdates);
          const items = state.reviewUpdates;
          const size = state.size_page
          const page = state.pageReviewUpdates
          const start_row = (page-1) * size
          const end_row = ((page-1) * size)+size

          const pageTotal = state.reviewUpdatesAll.length;

          const newPage = Math.ceil((pageTotal -1) / size)

          // update current items in table view
          const realItems = state.reviewUpdatesAll.slice(start_row,end_row)
          if (realItems.length > items.length) {
            items.push(realItems[items.length -1]);
          }

          // update pagination
          // state.reviewUpdates = items;
          state.reviewUpdates = _.clone(items); // prevent non-update on view
          if (newPage < page) {
            commit('setPageReviewUpdates', newPage)
          }

          // update page total
          commit('setReviewUpdatesTotal', pageTotal);

          //*/
        });
      });
    },
    async getReviewUpdates({commit,rootState,dispatch,state}:any, user:any){
      commit('setReviewUpdatesLoading', true)
      state.reviewUpdates = []
      state.reviewUpdatesAll = []

      let items: Array<any> = [];
      const reviewUpdatesByKey :Array<any> = [];

      // @todo use from another
      const company = await getCompany(user.company)//(await firebase.database().ref(`company/${user.company}`).once('value')).val();
      const allUser =await getUserAll(user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(user.company).once('value')).val();
      // console.log(allUser, "allUser");
      const teamByKey = rootState.newmember.teamByKey;

      // const $utils = Vue.prototype.$utilities;

      let query = firebase.firestore()
        .collection('integrations')
        .where('company','==', user.company);

      if (!state.reviewUpdatesIgnorePendingStatus) {
        query = query.where('status','==', 'pending')
      }

      await query
        // .orderBy('createdate', 'desc') // affected to pagination when press next page
        .get()
        .then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
              // console.log(doc.id, " => ", doc.data());
              let data = doc.data();

              // @todo fields: pic, firstname, lastname
              const email =  _.get(data, "email", "");
              const emailkey = email.replace(/\./g, "||");

              const applyUserInfo = (email) => {
                const emailkey = email.replace(/\./g, "||");
                data.firstname = _.get(allUser, `${emailkey}.firstname`, "");
                data.lastname = _.get(allUser, `${emailkey}.lastname`, "");
                data.name = `${data.firstname} ${data.lastname} ${email}`;
              }

              const applyRequestedBy = (email) => {
                if (!email) return;
                const emailkey = email.replace(/\./g, "||");
                const firstname = _.get(allUser, `${emailkey}.firstname`, "");
                const lastname = _.get(allUser, `${emailkey}.lastname`, "");
                data.requested_by = `${firstname} ${lastname.length ? lastname[0] : ""}`;
              }

              if (!_.get(data, "firstname") || !_.get(data, "lastname")) {
                applyUserInfo(email);
              }

              data.key = doc.id;

              // important data for edit in table
              data.name = `${data.firstname} ${data.lastname} ${data.email}`;
              data.manager = "";
              data.country = _.get(company, "country", "TH");
              data.language = data.country == "TH" ? "th" : "en";
              data.archived_type = "";

              // for read only input
              if (data.source != "hris") {
                data.manager_name = "";
                data.team_name = "";
              }

              let managerOption = null;
              if (data.source == "app") {
                // @fixed data mapping and getting requirements
                if (data.type == "direct_manager") {
                  const _email = _.get(data, "email", "");
                  const _email_request = _.get(data, "email_request", "");
                  data.email = _email_request;
                  data.manager = _email;

                  const bossKey = data.manager.replace(/\./g, "||");
                  const bossInfo = _.get(allUser, `${bossKey}`, {});
                  data.team = _.get(bossInfo, "team", "");

                  applyUserInfo(data.email);
                  applyRequestedBy(_email_request);
                } else if (data.type == "re_assign") {
                  const _email_request = _.get(data, "email_request", "");
                  data.team = "";
                  applyUserInfo(data.email);
                  applyRequestedBy(_email_request);
                } else if (data.type == "add_member") {
                  const _email_request = _.get(data, "email_request", "");
                  data.manager = _email_request;
                  applyRequestedBy(_email_request);
                } else if (data.type == "remove") {
                  applyUserInfo(data.email);
                  applyRequestedBy(data.email_request);

                  // team_name, manager_name, requested_by
                  const userInfo = _.get(allUser, emailkey, {});
                  data.team = _.get(userInfo, "team", "");
                  data.manager = _.get(userInfo, "boss", "");
                }

              } else if (data.source == "hris") {
                if (_.has(data, "direct_manager_email")) {
                  data.manager = _.get(data, "direct_manager_email", "");
                }
                if (_.has(data, "team")) {
                  data.team = _.get(data, "team", "");
                }
                if (data.type == "delete") {
                  data.manager = _.get(allUser, `${emailkey}.boss`, "");
                  data.team = _.get(allUser, `${emailkey}.team`, "");
                } else if (data.type == "create") {
                  data.manager = _.get(data, `manager_email`, "");
                  data.team_name = _.get(data, `team_name`, "");
                  if (data.team_name) {
                    data.team = _.findKey(teamByKey, {name: data.team_name});
                  }
                }
              }

              if (!_.has(data, "team")) {
                data.team = "";
              }

              if (data.manager) {
                managerOption = _.find(allUser, {email: data.manager});
                data.manager_name = managerOption ? 
                  `${_.get(managerOption, "firstname", "")} ${_.get(managerOption, "lastname", "")}`: 
                  data.manager;
              }

              if (data.team) {
                data.team_name = _.has(teamByKey, data.team) ?
                  _.get(teamByKey, `${data.team}.name`, "") :
                  data.team;
              }

              // resolve default manager label
              /*
              if (data.manager) {
                const managerIndex = _.findIndex(managerOptions, function(o) { return o.email == data.manager; });
                // console.log(data.manager, managerIndex);
                data.manager = managerIndex;
              }
              //*/
              

              items.push(data);
              reviewUpdatesByKey[doc.id] = data;
            })
          });

      commit('setReviewUpdatesByKey', reviewUpdatesByKey);
      commit('setReviewUpdatesAll', _.clone(items));
      commit('setReviewUpdatesTotal', items.length);
      items = _.sortBy(items.slice(0, state.size_page), [function(o:any) { return o.email; }]);

      commit('setPageReviewUpdates', 1);
      commit('setReviewUpdates', items);

      commit('setReviewUpdatesLoading', false);
    },
    sortReviewUpdates({commit,state}:any,value:any){
      //*
      var member: Array<any> = [];
      member.push(state.reviewUpdatesAll)
      /*
      if (state.inputSearchArchive.length > 2){
        if (member.length > 0){
          var re = new RegExp(state.inputSearchArchive, 'gi')
          var response = _.filter(member[0], (item): any => {
            if (item.firstname.match(re)) {
              return true
            } else if (item.lastname != undefined && item.lastname.match(re)) {
              return true
            } else if (item.value != undefined && (item.value.match(re))) {
              return true
            } else if (item.email != undefined && item.email.match(re)) {
              return true
            } else if (item.team_name != undefined && item.team_name.match(re)) {
              return true
            } else if (item.boss_firstname != undefined && item.boss_firstname.match(re)) {
              return true
            } else if (item.boss_lastname != undefined && item.boss_lastname.match(re)) {
              return true
            } else if (item.boss_name != undefined && item.boss_name.match(re)) {
              return true
            }
          });
          member = []
          member.push(response)
        }
      }
      //*/

      var size = state.size_page
      var page = state.pageReviewUpdates
      var field  = value[0]
      var order = value[1]
      var start_row = (page-1) * size
      var end_row = ((page-1) * size)+size

      // if(field == 'name')
      //   field = 'value'

      if(order == 'ascending' || order == 'asc'){
        if(field == 'exitdated'){
          order = 'desc'
        }else {
          order = 'asc'
        }
      }else {
        if(field == 'exitdated'){
          order = 'asc'
        }else{
          order = 'desc'
        }
      }
      var _users = _.orderBy(member[0], [field], [order]);
      commit('setSortfieldReviewUpdates', field)
      commit('setSortorderReviewUpdates', order)
      commit('setReviewUpdates', _users.slice(start_row,end_row))
      //*/
    },
    reviewUpdatesCurrentChange({commit,state}:any,page:any){
      var size = state.size_page
      var start_row = (page-1) * size
      var end_row = ((page-1) * size)+size
      let member: Array<any> = [];
      member.push(state.reviewUpdatesAll)
      // if (state.inputSearchArchive.length > 2){
      //   if (member.length > 0){
      //     var re = new RegExp(state.inputSearchArchive, 'gi')
      //     var response = _.filter(member[0], (item): any => {
      //       if (item.firstname.match(re)) {
      //         return true
      //       } else if (item.lastname != undefined && item.lastname.match(re)) {
      //         return true
      //       } else if (item.value != undefined && (item.value.match(re))) {
      //         return true
      //       } else if (item.email != undefined && item.email.match(re)) {
      //         return true
      //       } else if (item.team_name != undefined && item.team_name.match(re)) {
      //         return true
      //       } else if (item.boss_firstname != undefined && item.boss_firstname.match(re)) {
      //         return true
      //       } else if (item.boss_lastname != undefined && item.boss_lastname.match(re)) {
      //         return true
      //       } else if (item.boss_name != undefined && item.boss_name.match(re)) {
      //         return true
      //       }
      //     });
      //     member[0] = response
      //   }
      // }
      
      var field = state.sortfieldreviewupdates
      var _users = _.orderBy(member[0], [field], [state.sortorderreviewupdates]);
      commit('setPageReviewUpdates', page)
      commit('setReviewUpdates', _users.slice(start_row, end_row))
    },

    /* import members: v1.0.0 */

    // data is array of object from csv file or xls(x) file
    async legacyValidateNewMembersWithUpdate({commit,state,rootState}:any, data:any){
      // console.log("validateNewMembersWithUpdate()");
      // console.log(_.cloneDeep(data), "data");

      // @todo allow to don't use header

      // @see https://trello.com/c/9SSAaYG4/2048-members-as-hr-i-want-to-bulk-import-new-members-into-happily-so-i-dont-have-to-rely-on-manual-individual-entries

      // Bulk Import Validator and Conflict Resolver for Employee Engagement Platform

      // Utility: Normalize imported row
      const normalizeMember = (member:any) => {
        return {
          first_name: member.firstname.trim(),
          last_name: member.lastname.trim(),
          email: member.email.toLowerCase().trim(),
          team_name: _.get(member, "team_name", "").trim(),
          direct_manager_email: member.boss.toLowerCase().trim(),
          manager_name: "",
          role: member.user_type || "user",
          language: member.language || "th",
          country: member.country || "TH",
          time_zone: member.timezone || 7
        };
      }

      // Utility: Extract initials from name
      const getInitials = (firstName:string, lastName:string) => {
        return `${firstName && firstName[0] || ''}${lastName && lastName[0] || ''}`.toUpperCase();
      }

      // Utility: Suggest new team name using manager initials
      const suggestTeamNameConflict = (originalTeamName:string, managerInitials:string) => {
        return `${originalTeamName} (${managerInitials})`;
      }

      // Stub: Rename team entity before assignment to preserve historical data
      const renameTeam = (oldName:string, newName:string) => {
        // console.log(`Renaming team: '${oldName}' → '${newName}'`);
        // TODO: Implement backend update for team rename
      }

      // Main validation function with two-pass dependency resolution
      const validateNewMembersWithUpdate = (existingMembers:any, newMembers:any) => {
        const existingByEmail = new Map();
        const teamToManager = new Map();
        const emailToManager = new Map();
        const managerNameMap = new Map();
        const renamedTeams = new Set();

        const teamHasMembers = new Map();
        const emailToPreviousManager = new Map();
        let teamHasMoreThanOneManager = new Map();
        for (const m of existingMembers) {
          const normalized = normalizeMember(m);
          if (!teamHasMembers.has(normalized.team_name)) {
            teamHasMembers.set(normalized.team_name, []);
          }
          teamHasMembers.set(normalized.team_name, [...teamHasMembers.get(normalized.team_name), normalized.email]);
          emailToPreviousManager.set(normalized.email, normalized.direct_manager_email);

          if (!teamHasMoreThanOneManager.has(normalized.team_name)) {
            teamHasMoreThanOneManager.set(normalized.team_name, []);
          }
          if (_.indexOf(teamHasMoreThanOneManager.get(normalized.team_name), normalized.direct_manager_email) === -1) {
            teamHasMoreThanOneManager.get(normalized.team_name).push(normalized.direct_manager_email);
          }
        }

        // Pass 1: Index existing and new members
        for (const m of existingMembers) {
          const normalized = normalizeMember(m);
          existingByEmail.set(normalized.email, normalized);

          if (teamHasMembers.has(normalized.team_name)) {
            const teamMembers = teamHasMembers.get(normalized.team_name);
            const index = _.indexOf(teamMembers, normalized.direct_manager_email);
            if (index !== -1) {
              teamToManager.set(normalized.team_name, normalized.direct_manager_email);
            }
          }

          emailToManager.set(normalized.email, normalized.direct_manager_email);
          managerNameMap.set(normalized.email, {
            first_name: normalized.first_name,
            last_name: normalized.last_name
          });
        }

        const newByEmail = new Map();
        for (const m of newMembers) {
          const normalized = normalizeMember(m);
          newByEmail.set(normalized.email, normalized);
          managerNameMap.set(normalized.email, {
            first_name: normalized.first_name,
            last_name: normalized.last_name
          });
        }

        interface ErrorInterface {
          row: number;
          email: string;
          error: string;
          recommended_fix: string;
          auto_fix?: object;
        }

        const errors: ErrorInterface[] = [];
        const validMembers: any[] = [];

        // Pass 2: Validate with full visibility
        for (const [index, raw] of newMembers.entries()) {
          const row = normalizeMember(raw);
          const rowIndex = index + 1;
          const memberErrors: ErrorInterface[] = [];

          const email = row.email;
          const managerEmail = row.direct_manager_email;
          const teamName = _.get(row, "team_name", "").trim();
          const isExisting = existingByEmail.has(email);
          const existing = existingByEmail.get(email);

          const firstName = _.get(row, "first_name", "").trim();
          const lastName = _.get(row, "last_name", "").trim();

          if (!firstName) {
            memberErrors.push({
              row: rowIndex,
              email,
              error: "First name is required",
              recommended_fix: `Please provide a valid first name`
            });
            continue;
          }

          if (!lastName) {
            memberErrors.push({
              row: rowIndex,
              email,
              error: "Last name is required",
              recommended_fix: `Please provide a valid last name`
            });
            continue;
          }

          // Team name check
          if (!teamName) {
            memberErrors.push({
              row: rowIndex,
              email,
              error: "Team name is required",
              recommended_fix: `Please provide a valid team name`
            });
            continue;
          }

          // Manager existence check (updated)
          const managerExists = existingByEmail.has(managerEmail) || newByEmail.has(managerEmail);
          if (!managerExists) {
            memberErrors.push({
              row: rowIndex,
              email,
              error: "Manager does not exist",
              recommended_fix: `Ensure manager (${managerEmail}) is in system or included in import`
            });
            continue;
          }

          // Manager consistency check
          if (isExisting) {
            const prevManager = existing.direct_manager_email;
            if (prevManager !== managerEmail) {
              emailToManager.set(email, managerEmail); // Accept update
            }
          }

          // Team name conflict check
          if (teamToManager.has(teamName)) {
            const currentManager = teamToManager.get(teamName);
            

            let changed = currentManager !== managerEmail;
            if (changed) {
              const previousManager = emailToPreviousManager.get(email);
              if (previousManager && previousManager == managerEmail) {
                changed = false;
                // console.log(`email: ${email}, current manager: ${currentManager}, new manager: ${managerEmail}, previous manager: ${previousManager}`);
              }
            }

            if (changed) {
              const managerName = managerNameMap.get(managerEmail) || { first_name: '', last_name: '' };
              const initials = getInitials(managerName.first_name, managerName.last_name);
              const suggestedName = suggestTeamNameConflict(teamName, initials);

              if (!renamedTeams.has(suggestedName)) {
                renameTeam(teamName, suggestedName);
                renamedTeams.add(suggestedName);
              }

              row.team_name = suggestedName;
              teamToManager.set(suggestedName, managerEmail);

              if (!teamHasMoreThanOneManager.has(suggestedName)) {
                teamHasMoreThanOneManager.set(suggestedName, []);
              }
              if (_.indexOf(teamHasMoreThanOneManager.get(suggestedName), managerEmail) === -1) {
                teamHasMoreThanOneManager.get(suggestedName).push(managerEmail);
              }

              memberErrors.push({
                row: rowIndex,
                email,
                error: `Team '${teamName}' already used by another manager`,
                recommended_fix: `Renamed to '${suggestedName}'`,
                auto_fix: { team_name: suggestedName }
              });
            } else {
              if (!teamHasMoreThanOneManager.has(teamName)) {
                teamHasMoreThanOneManager.set(teamName, []);
              }
              if (_.indexOf(teamHasMoreThanOneManager.get(teamName), managerEmail) === -1) {
                teamHasMoreThanOneManager.get(teamName).push(managerEmail);
              }
            }
          } else {
            teamToManager.set(teamName, managerEmail);

            if (!teamHasMoreThanOneManager.has(teamName)) {
              teamHasMoreThanOneManager.set(teamName, []);
            }
            if (_.indexOf(teamHasMoreThanOneManager.get(teamName), managerEmail) === -1) {
              teamHasMoreThanOneManager.get(teamName).push(managerEmail);
            }
          }

          const manager = managerNameMap.get(managerEmail);
          row.manager_name = manager.first_name + " " + manager.last_name;


          if (!teamHasMembers.has(row.team_name)) {
            teamHasMembers.set(row.team_name, []);
          }
          if (_.indexOf(teamHasMembers.get(row.team_name), row.email) === -1) { 
            teamHasMembers.get(row.team_name).push(row.email);
          }

          validMembers.push(row);
          if (memberErrors.length > 0) errors.push(...memberErrors);
        }

        // >= 2 managers
        let tmp = new Map();
        for (const [key, value] of teamHasMoreThanOneManager.entries()) {
          if (value.length > 1) {
            tmp.set(key, value);
          }
        }
        teamHasMoreThanOneManager = tmp;
        
        const findTopManager = (email:string, emailToManager:any) => {
          let current = email;
          const visited = new Set();
          while (emailToManager.get(current)) {
            if (visited.has(current)) {
              throw new Error('Loop detected in manager hierarchy');
            }
            visited.add(current);
            current = emailToManager.get(current);
          }
          return current;
        }
        const findMember = (email:string, members:any[]) => {
          return _.find(members, (item:any) => {
            return item.email == email;
          });
        }

        for (const num in validMembers) {
          const member = validMembers[num];
          const teamName = member.team_name;
          const managerEmail = member.direct_manager_email;

          const email = member.email;
          const existing = _.find(existingMembers, (item:any) => {
            return item.email == email;
          });

          let isUpdated = false;
          if (existing) {
            const isManagerChanged = existing.boss != member.direct_manager_email;
            const isTeamChanged = existing.team_name != member.team_name;
            if (!isManagerChanged && !isTeamChanged) {
              isUpdated = true;
            }
          }
          /* if (!isUpdated) continue; */

          const topManager = findTopManager(member.email, emailToManager);
          const managers = teamHasMoreThanOneManager.get(teamName);
          const members = teamHasMembers.get(teamName);
          const innerManagers = _.intersection(managers, members);

          if (teamHasMoreThanOneManager.has(teamName)) {

            // Special case: No update but need to rename team
            // There are 2 managers, topManager is not in the team and lower manager is in the team. It is not only lower manager who is under top manager.
            const managerHasChildCountMap = new Map();
            for (const manager of managers) {
              managerHasChildCountMap.set(manager, 0);
              for (const member of members) {
                const item = _.find(validMembers, (item:any) => {
                  return item.email == member;
                });
                if (!item) continue;
                if (item.direct_manager_email == manager) {
                  managerHasChildCountMap.set(manager, managerHasChildCountMap.get(manager) + 1);
                }
              }
            }
            const rowIndex = _.findIndex(newMembers, function(o) { return o.email == email; });
            const invalidTopManager = managerHasChildCountMap.get(topManager) > 1;

            if (topManager != managerEmail) {
              if (innerManagers.length > 1) {
                if (_.indexOf(innerManagers, managerEmail) !== -1) {
                  const debug = false;
                  debug && console.group(member.email, teamName);
                  debug && console.log(`${member.email} in team ${teamName} has more than 1 manager`);
                  debug && console.log(`${member.email}, top: ${topManager}, set: ${managerEmail}`);
                  debug && console.log(`${teamName}, managers: `, managers);
                  debug && console.log(`${teamName}, members: `, members);
                  debug && console.log('innser managers: ', innerManagers);

                  // if manager has child, reject manager and manager is yourself
                  let hasChild = false;
                  for (const childEmail of members) {
                    const existingChild = findMember(childEmail, validMembers);
                    debug && console.log(existingChild.direct_manager_email, member.email);
                    if (existingChild.direct_manager_email == member.email) {
                      hasChild = true;
                      break;
                    }
                  }
                  const realManager = hasChild ? member.email : managerEmail;
                  debug && console.log('hasChild: ', hasChild);
                  debug && console.log('realManager: ', realManager);
                  const managerName = managerNameMap.get(realManager) || { first_name: '', last_name: '' };
                  const initials = getInitials(managerName.first_name, managerName.last_name);
                  const suggestedName = suggestTeamNameConflict(teamName, initials);
                  debug && console.log(`CHANGE: teamName: ${teamName} -> ${suggestedName}`);
                  debug && console.groupEnd();

                  debug && console.log(`email: ${email}, rowIndex: ${rowIndex}`);

                  errors.push({
                    row: rowIndex + 1,
                    email: member.email,
                    error: `Team '${teamName}' already used by another manager`,
                    recommended_fix: `Renamed to '${suggestedName}'`,
                    auto_fix: { team_name: suggestedName }
                  });
                }
              } else if (innerManagers.length == 1) {
                if (invalidTopManager) {
                  // console.log('invalidTopManager 1: !');
                  // console.log(`email: ${email}, teamName: ${teamName}, topManager: ${topManager}, managerEmail: ${managerEmail}`);

                  const managerName = managerNameMap.get(managerEmail) || { first_name: '', last_name: '' };
                  const initials = getInitials(managerName.first_name, managerName.last_name);
                  const suggestedName = suggestTeamNameConflict(teamName, initials);
                  errors.push({
                    row: rowIndex + 1,
                    email: member.email,
                    error: `Team '${teamName}' already used by another manager`,
                    recommended_fix: `Renamed to '${suggestedName}'`,
                    auto_fix: { team_name: suggestedName }
                  });
                }
              }
            } else {
              if (invalidTopManager && (_.indexOf(managers, email) !== -1)) {
                // console.log('invalidTopManager 2: !');
                // console.log(`email: ${email}, teamName: ${teamName}, topManager: ${topManager}, managerEmail: ${managerEmail}`);

                const managerName = managerNameMap.get(email) || { first_name: '', last_name: '' };
                const initials = getInitials(managerName.first_name, managerName.last_name);
                const suggestedName = suggestTeamNameConflict(teamName, initials);
                errors.push({
                  row: rowIndex + 1,
                  email: member.email,
                  error: `Team '${teamName}' already used by another manager`,
                  recommended_fix: `Renamed to '${suggestedName}'`,
                  auto_fix: { team_name: suggestedName }
                });
              }
            }
          }
        }

        return { validMembers, errors };
      }

      // const columns = data.shift();
      // console.log(columns, "columns");
      const header = data.shift();

      const user = rootState.authen.user;
      const company = await getCompany(user.company)

      const findIndex = (keyword:any, defaults: number) => {
        const index = header.findIndex((item:any) => item.toLowerCase().includes(keyword));
        return index !== -1 ? index : defaults;
      }
      const toGMTOffset = (input:any) => {
        if (typeof input === 'string') {
          // Normalize input like "+7:00", "7:00", "-3:00" to number
          const match = input.match(/^([+-]?)(\d{1,2})(:00)?$/);
          if (match) {
            const sign = match[1] === '-' ? -1 : 1;
            const hours = parseInt(match[2], 10);
            input = sign * hours;
          }
        }
      
        const num = Number(input);
      
        if (isNaN(num) || num < -12 || num > 14) {
          throw new Error("Invalid GMT offset. Must be between -12 and 14.");
        }
      
        const sign = num >= 0 ? '+' : '-';
        const absNum = Math.abs(num);
        const hours = String(Math.floor(absNum)).padStart(2, '0');
      
        return `${sign}${hours}:00`;
      }
      const HEADER_INDEX = {
        firstname: findIndex("first name", 0),
        lastname: findIndex("last name", 1),
        email: findIndex("email address", 2),
        team_name: findIndex("team name", 3),
        boss: findIndex("direct manager", 4),
        language: findIndex("language", 5),
        timezone: findIndex("time-zone", findIndex("timezone", 6)),
        country: findIndex("country", 7),
        user_type: findIndex("user type", 8),
      };

      // UPDATE: team_name, boss
      // const allTeam = (await firebase.database().ref('team').orderByChild("company").equalTo(user.company).once('value')).val()
      // const allUser =(await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val();
      // for (const emailKey in allUser) {
      //   const user = _.get(allUser, emailKey);
      //   const team = _.get(user, "team");
      //   if (team && _.has(allTeam, team)) {
      //     _.set(allUser, `${emailKey}.team_name`, _.get(allTeam, `${team}.name`, ""));
      //   }
      // }
      // const existingMembers = _.values(allUser);

      const existingMembers = _.values(_.get(rootState, "main.allUser", []));

      const newMembers: any[] = _.map(data, (row:any) => {
        const newMember = {
          firstname: _.get(row, HEADER_INDEX.firstname, ""),
          lastname: _.get(row, HEADER_INDEX.lastname, ""),
          email: _.get(row, HEADER_INDEX.email, ""),
          team_name: _.get(row, HEADER_INDEX.team_name, ""),
          boss: _.get(row, HEADER_INDEX.boss, ""),
          user_type: _.get(row, HEADER_INDEX.user_type, "user"),
          language:  _.get(row, HEADER_INDEX.language, company.country == "TH" ? "th" : "en"),
          country: _.get(row, HEADER_INDEX.country, company.country || "TH") ,
          timezone: _.get(row, HEADER_INDEX.timezone, company.timezone || 7),
        };

        // filter language
        const language = newMember.language.toLowerCase();
        if (language == "th" || language == "thai" || language == "ไทย") {
          newMember.language = "th";
        } else if (language == "en" || language == "eng" || "english" || language == "อังกฤษ") {
          newMember.language = "en";
        }

        // filter timezone
        newMember.timezone = toGMTOffset(newMember.timezone);

        return newMember;
      });

      const { validMembers, errors } = validateNewMembersWithUpdate(existingMembers, newMembers);

      // merge validMembers and errors
      for (const error of errors) {
        const row = _.get(validMembers, `[${error.row - 1}]`);
        if (!row) continue;
        _.set(validMembers, `[${error.row - 1}].note`, error.recommended_fix);
        _.set(validMembers, `[${error.row - 1}].error`, error.error);
      }

      // console.log(_.cloneDeep(validMembers), "validMembers");
      
      // Data Transformation
      // 1. ✅ 3 New Members to Add
      // 2. 🟡 2 Members to Update
      // 3. ❌ 1 Members to Archive
      // 4. ⚠️ 2 Issues to Resolve
      const dataGroups: any[] = [{
        name: "New Members to Add",
        code: "new",
        items: []
      }, {
        name: "Members to Update",
        code: "update",
        items: []
      }, {
        name: "Members to Archive",
        code: "archive",
        items: []
      }, {
        name: "Issues to Resolve",
        code: "resolve",
        items: []
      }];

      const INDEX_NEW = 0;
      const INDEX_UPDATE = 1;
      // const INDEX_ARCHIVE = 2;
      const INDEX_RESOLVE = 3;

      const allArchiveMember = (await firebase.database().ref('users_employee_exit').orderByChild("company").equalTo(user.company).once('value')).val();

      let total = 0;
      for (const member of validMembers) {
        const email = member.email;
        const existing = _.find(existingMembers, (item:any) => {
          return item.email == email;
        });

        // Don't add user on archive list
        const emailKey = email.replace(/\./g, "||");
        const isArchived = _.has(allArchiveMember, emailKey);
        if (isArchived) continue;

        // Update existing member
        let isUpdated = false;
        if (existing) {
          const isManagerChanged = existing.boss != member.direct_manager_email;
          const isTeamChanged = existing.team_name != member.team_name;
          isUpdated = isManagerChanged || isTeamChanged;
        }

        const hasError = _.has(member, "error");
        if (!isUpdated) {
          if (!hasError) {
            continue;
          }
        }
        
        if (!hasError) {
          _.set(member, "note", "Ready to import");
        }

        if (existing) {
          const index = isUpdated ? INDEX_UPDATE : INDEX_RESOLVE;
          dataGroups[index].items.push(member);
        } else {
          dataGroups[INDEX_NEW].items.push(member);
        }
        total++;
      }

      commit('setImportMembersTableDataGroupsTotal', total);
      commit('setImportMembersTableDataGroups', dataGroups);

      commit('setOrigImportMembersTableDataGroupsTotal', total);
      commit('setOrigImportMembersTableDataGroups', _.cloneDeep(dataGroups));
    },
    async validateNewMembersWithUpdate({commit,rootState}:any, data:any){
      const rows = _.cloneDeep(Array.isArray(data) ? data : [])
      const header = rows.shift()

      if (!Array.isArray(header)) {
        commit('setImportMembersTableDataGroupsTotal', 0)
        commit('setImportMembersTableDataGroups', [])
        commit('setOrigImportMembersTableDataGroupsTotal', 0)
        commit('setOrigImportMembersTableDataGroups', [])
        return
      }

      const user = rootState.authen.user
      const company = await getCompany(user.company)
      const companyCountry = company.country || 'TH'
      const defaultLanguage = companyCountry === 'TH' ? 'th' : 'en'
      const defaultTimezone = normalizeImportTimezone(company.timezone || 7, 7).value

      const HEADER_INDEX = {
        firstname: findImportHeaderIndex(header, ['first name'], -1),
        lastname: findImportHeaderIndex(header, ['last name'], -1),
        email: findImportHeaderIndex(header, ['email address', 'email'], -1),
        team_name: findImportHeaderIndex(header, ['team name'], -1),
        boss: findImportHeaderIndex(header, ['direct manager email', 'direct manager', 'manager email'], -1),
        language: findImportHeaderIndex(header, ['language'], -1),
        country: findImportHeaderIndex(header, ['country'], -1),
        timezone: findImportHeaderIndex(header, ['time-zone', 'timezone', 'time zone'], -1),
        user_type: findImportHeaderIndex(header, ['user type', 'role'], -1),
        nickname: findImportHeaderIndex(header, ['nickname'], -1),
        birthday: findImportHeaderIndex(header, ['birthday', 'birth date'], -1),
        join_date: findImportHeaderIndex(header, ['join date', 'start date', 'work date'], -1),
      }

      const headerPresence = {
        firstname: HEADER_INDEX.firstname !== -1,
        lastname: HEADER_INDEX.lastname !== -1,
        email: HEADER_INDEX.email !== -1,
        team_name: HEADER_INDEX.team_name !== -1,
        boss: HEADER_INDEX.boss !== -1,
        language: HEADER_INDEX.language !== -1,
        country: HEADER_INDEX.country !== -1,
        timezone: HEADER_INDEX.timezone !== -1,
        user_type: HEADER_INDEX.user_type !== -1,
        nickname: HEADER_INDEX.nickname !== -1,
        birthday: HEADER_INDEX.birthday !== -1,
        join_date: HEADER_INDEX.join_date !== -1,
      }

      const isProfileOnlyImport =
        HEADER_INDEX.email !== -1 &&
        HEADER_INDEX.firstname === -1 &&
        HEADER_INDEX.lastname === -1 &&
        HEADER_INDEX.team_name === -1 &&
        HEADER_INDEX.boss === -1 &&
        HEADER_INDEX.language === -1 &&
        HEADER_INDEX.country === -1 &&
        HEADER_INDEX.timezone === -1 &&
        HEADER_INDEX.user_type === -1 &&
        (headerPresence.nickname || headerPresence.birthday || headerPresence.join_date)

      if (HEADER_INDEX.email === -1) {
        commit('setImportMembersTableDataGroupsTotal', 1)
        commit('setImportMembersTableDataGroups', [{
          name: 'Issues to Resolve',
          code: 'resolve',
          items: [{
            row: 1,
            email: '',
            team_name: '',
            manager_name: '',
            error: 'Email Address column is required',
            note: 'Include an Email Address column header so members can be matched correctly.',
          }]
        }])
        commit('setOrigImportMembersTableDataGroupsTotal', 1)
        commit('setOrigImportMembersTableDataGroups', _.cloneDeep([{
          name: 'Issues to Resolve',
          code: 'resolve',
          items: [{
            row: 1,
            email: '',
            team_name: '',
            manager_name: '',
            error: 'Email Address column is required',
            note: 'Include an Email Address column header so members can be matched correctly.',
          }]
        }]))
        return
      }

      const existingMembers = _.values(_.get(rootState, 'main.allUser', []))
      const teamByKey = _.get(rootState, 'newmember.teamByKey', {})
      const existingByEmail = new Map()
      existingMembers.forEach((member: any) => {
        const email = normalizeImportEmail(member.email)
        if (!email) return
        const teamName = normalizeImportString(member.team_name || _.get(teamByKey, `${member.team}.name`, ''))
        existingByEmail.set(email, {
          first_name: normalizeImportString(member.firstname),
          last_name: normalizeImportString(member.lastname),
          email,
          team_name: teamName,
          direct_manager_email: normalizeImportEmail(member.boss),
          manager_name: '',
          user_type: normalizeImportString(member.user_type) || 'user',
          language: normalizeImportLanguage(member.language, defaultLanguage),
          country: normalizeImportCountry(member.country, companyCountry),
          time_zone: normalizeImportTimezone(_.get(member, 'timezone', defaultTimezone), defaultTimezone).value,
          nickname: normalizeImportString(member.nickname),
          birthday: normalizeStoredBirthday(member.birthday),
          workdate: normalizeStoredWorkdate(member.workdate),
        })
      })

      const importedMembers: any[] = []
      const importedEmailCount = new Map()

      const addIssue = (member: any, error: string, recommendedFix: string) => {
        const issues = _.get(member, '_issues', [])
        if (!issues.some((item: any) => item.error === error && item.recommended_fix === recommendedFix)) {
          issues.push({ error, recommended_fix: recommendedFix })
          member._issues = issues
        }
      }

      const addWarning = (member: any, message: string) => {
        const warnings = _.get(member, '_warnings', [])
        if (warnings.indexOf(message) === -1) {
          warnings.push(message)
          member._warnings = warnings
        }
      }

      const addGlobalIssue = (error: string, recommendedFix: string, emails: string[] = []) => {
        let targets = importedMembers.filter((member) => emails.indexOf(member.email) !== -1)
        if (targets.length === 0 && importedMembers.length > 0) {
          targets = [importedMembers[0]]
        }
        targets.forEach((member) => addIssue(member, error, recommendedFix))
      }

      rows.forEach((row: any, index: number) => {
        const values = Array.isArray(row) ? row : []
        const email = normalizeImportEmail(_.get(values, HEADER_INDEX.email, ''))
        const existing = email ? existingByEmail.get(email) : null
        const importedFirstName = normalizeImportString(_.get(values, HEADER_INDEX.firstname, ''))
        const importedLastName = normalizeImportString(_.get(values, HEADER_INDEX.lastname, ''))
        const importedTeamName = normalizeImportString(_.get(values, HEADER_INDEX.team_name, ''))
        const importedManagerEmail = normalizeImportEmail(_.get(values, HEADER_INDEX.boss, ''))
        const importedNickname = normalizeImportString(_.get(values, HEADER_INDEX.nickname, ''))
        const birthdayInput = _.get(values, HEADER_INDEX.birthday, '')
        const joinDateInput = _.get(values, HEADER_INDEX.join_date, '')
        const timezoneResult = normalizeImportTimezone(
          _.get(values, HEADER_INDEX.timezone, ''),
          _.get(existing, 'time_zone', defaultTimezone)
        )
        const birthdayResult = normalizeImportBirthday(birthdayInput)
        const joinDateResult = normalizeImportDate(joinDateInput)
        const hasNicknameUpdate = headerPresence.nickname && importedNickname !== ''
        const hasBirthdayUpdate = headerPresence.birthday && normalizeImportString(birthdayInput) !== ''
        const hasWorkdateUpdate = headerPresence.join_date && normalizeImportString(joinDateInput) !== ''

        const member = {
          row: index + 1,
          first_name: headerPresence.firstname ? importedFirstName : _.get(existing, 'first_name', ''),
          last_name: headerPresence.lastname ? importedLastName : _.get(existing, 'last_name', ''),
          email,
          team_name: headerPresence.team_name ? importedTeamName : _.get(existing, 'team_name', ''),
          direct_manager_email: headerPresence.boss ? importedManagerEmail : _.get(existing, 'direct_manager_email', ''),
          manager_name: '',
          user_type: headerPresence.user_type
            ? normalizeImportString(_.get(values, HEADER_INDEX.user_type, '')) || _.get(existing, 'user_type', '') || 'user'
            : _.get(existing, 'user_type', '') || 'user',
          language: headerPresence.language
            ? normalizeImportLanguage(_.get(values, HEADER_INDEX.language, ''), _.get(existing, 'language', defaultLanguage))
            : _.get(existing, 'language', defaultLanguage),
          country: headerPresence.country
            ? normalizeImportCountry(_.get(values, HEADER_INDEX.country, ''), _.get(existing, 'country', companyCountry))
            : _.get(existing, 'country', companyCountry),
          time_zone: timezoneResult.value,
          nickname: hasNicknameUpdate ? importedNickname : _.get(existing, 'nickname', ''),
          birthday: hasBirthdayUpdate ? birthdayResult.value : _.get(existing, 'birthday', ''),
          workdate: hasWorkdateUpdate ? joinDateResult.value : _.get(existing, 'workdate', ''),
          _issues: [],
          _warnings: [],
          _profile_only: isProfileOnlyImport,
          _existing: existing ? _.cloneDeep(existing) : null,
          _column_presence: _.cloneDeep(headerPresence),
          _optional_fields: {
            nickname: hasNicknameUpdate,
            birthday: hasBirthdayUpdate,
            workdate: hasWorkdateUpdate,
          },
        }

        const hasAnyValue = [
          importedFirstName,
          importedLastName,
          email,
          importedTeamName,
          importedManagerEmail,
          importedNickname,
          normalizeImportString(birthdayInput),
          normalizeImportString(joinDateInput),
        ].some(Boolean)
        if (!hasAnyValue) return

        if (timezoneResult.error) {
          addIssue(member, 'Timezone is invalid', timezoneResult.error)
        }
        if (member._optional_fields.birthday && birthdayResult.error) {
          addIssue(member, 'Birthday is invalid', birthdayResult.error)
        }
        if (member._optional_fields.workdate && joinDateResult.error) {
          addIssue(member, 'Join date is invalid', joinDateResult.error)
        }

        importedMembers.push(member)
        if (member.email) {
          importedEmailCount.set(member.email, (importedEmailCount.get(member.email) || 0) + 1)
        }
      })

      importedMembers.forEach((member) => {
        if (!member.email) {
          addIssue(member, 'Email address is required', 'Please provide a valid email address.')
        } else if (!isValidImportEmail(member.email)) {
          addIssue(member, 'Email address is invalid', 'Use a valid work email address for each member.')
        }
        if (member.email && (importedEmailCount.get(member.email) || 0) > 1) {
          addIssue(member, 'Email address is duplicated in the import file', 'Keep only one row per email address in the import file.')
        }
        if (member._profile_only) {
          if (!existingByEmail.has(member.email)) {
            addIssue(member, 'Member does not exist', 'Profile-only imports can update existing members only.')
          }
          if (!member._optional_fields.nickname && !member._optional_fields.birthday && !member._optional_fields.workdate) {
            addIssue(member, 'No profile fields to update', 'Include at least one of Nickname, Birthday, or Join Date.')
          }
          return
        }
        if (!member.first_name) {
          addIssue(member, 'First name is required', 'Please provide a valid first name.')
        }
        if (!member.last_name) {
          addIssue(member, 'Last name is required', 'Please provide a valid last name.')
        }
        if (!member.team_name) {
          addIssue(member, 'Team name is required', 'Please provide a team name for each member.')
        }
      })

      const finalMembersByEmail = new Map(existingByEmail)
      importedMembers.forEach((member) => {
        if (!member.email) return
        const existing = existingByEmail.get(member.email) || {}
        finalMembersByEmail.set(member.email, {
          ...existing,
          ...member,
        })
      })

      importedMembers.forEach((member) => {
        if (member._profile_only) return
        if (!member.email || !member.direct_manager_email) return
        if (member.direct_manager_email === member.email) {
          addIssue(member, 'Manager assignment creates a reporting loop', 'A member cannot be their own direct manager.')
          return
        }
        if (!finalMembersByEmail.has(member.direct_manager_email)) {
          addIssue(
            member,
            'Manager does not exist',
            `Ensure manager ${member.direct_manager_email} already exists or is included in the same import.`
          )
        }
      })

      const reportCounts = new Map()
      finalMembersByEmail.forEach((member: any) => {
        if (!member.direct_manager_email) return
        reportCounts.set(member.direct_manager_email, (reportCounts.get(member.direct_manager_email) || 0) + 1)
      })

      const rootEmails: string[] = []
      finalMembersByEmail.forEach((member: any) => {
        if (!member.direct_manager_email) {
          rootEmails.push(member.email)
        }
      })
      if (!isProfileOnlyImport && rootEmails.length !== 1) {
        const targetEmails = importedMembers
          .filter((member) => !member.direct_manager_email)
          .map((member) => member.email)
        addGlobalIssue(
          'Exactly one person in the org may have no direct manager',
          'Leave the direct manager blank for only the single top-most person in the org.',
          targetEmails
        )
      }

      const visitState = new Map()
      const visitStack: string[] = []
      const cycleEmails = new Set()
      const visitManagerChain = (email: string) => {
        const currentState = visitState.get(email)
        if (currentState === 1) {
          const startIndex = visitStack.indexOf(email)
          visitStack.slice(startIndex).forEach((entry) => cycleEmails.add(entry))
          return
        }
        if (currentState === 2) return

        visitState.set(email, 1)
        visitStack.push(email)
        const member = finalMembersByEmail.get(email)
        const managerEmail = _.get(member, 'direct_manager_email', '')
        if (managerEmail && finalMembersByEmail.has(managerEmail)) {
          visitManagerChain(managerEmail)
        }
        visitStack.pop()
        visitState.set(email, 2)
      }
      finalMembersByEmail.forEach((_: any, email: string) => {
        visitManagerChain(email)
      })
      if (!isProfileOnlyImport && cycleEmails.size > 0) {
        const targetEmails = importedMembers
          .filter((member) => cycleEmails.has(member.email) || cycleEmails.has(member.direct_manager_email))
          .map((member) => member.email)
        addGlobalIssue(
          'Manager assignment creates a reporting loop',
          'Remove circular reporting relationships before importing.',
          targetEmails
        )
      }

      const managerTeams = new Map()
      if (!isProfileOnlyImport) {
        reportCounts.forEach((_: number, managerEmail: string) => {
          const managerMember = finalMembersByEmail.get(managerEmail)
          if (!managerMember) return

        if (!managerMember.team_name) {
          if (importedMembers.some((member: any) => member.email === managerEmail)) {
            addGlobalIssue(
              'Managers must have a team name',
              'Give each manager a team name before importing.',
              [managerEmail]
            )
          }
          return
        }

        const teamKey = managerMember.team_name.toLowerCase()
        if (!managerTeams.has(teamKey)) {
          managerTeams.set(teamKey, [])
        }
        managerTeams.get(teamKey).push(managerEmail)

        if (managerMember.team_name.length > 15 && importedMembers.some((member: any) => member.email === managerEmail)) {
          addWarning(
            importedMembers.find((member: any) => member.email === managerEmail),
            `Team name '${managerMember.team_name}' is longer than 15 characters. Shorter team names display better in the app.`
          )
        }
        })

        managerTeams.forEach((managerEmails: string[]) => {
          const uniqueManagers = _.uniq(managerEmails)
          if (uniqueManagers.length <= 1) return
          uniqueManagers.forEach((managerEmail) => {
            if (importedMembers.some((member: any) => member.email === managerEmail)) {
              const managerMember = finalMembersByEmail.get(managerEmail)
              addGlobalIssue(
                `Manager team name '${managerMember.team_name}' is already used elsewhere in the org`,
                'Give each manager-owned team a unique name.',
                [managerEmail]
              )
            }
          })
        })
      }

      importedMembers.forEach((member) => {
        const manager = finalMembersByEmail.get(member.direct_manager_email)
        member.manager_name = manager ? buildImportName(manager.first_name, manager.last_name) : ''
      })

      const dataGroups: any[] = [{
        name: 'New Members to Add',
        code: 'new',
        items: []
      }, {
        name: 'Members to Update',
        code: 'update',
        items: []
      }, {
        name: 'Members to Archive',
        code: 'archive',
        items: []
      }, {
        name: 'Issues to Resolve',
        code: 'resolve',
        items: []
      }]

      const allArchiveMember = (await firebase.database().ref('users_employee_exit').orderByChild('company').equalTo(user.company).once('value')).val()

      let total = 0
      importedMembers.forEach((member) => {
        const emailKey = member.email ? member.email.replace(/\./g, '||') : ''
        if (emailKey && _.has(allArchiveMember, emailKey)) {
          return
        }

        const existing = existingByEmail.get(member.email)
        const issueList = _.get(member, '_issues', [])
        const warningList = _.get(member, '_warnings', [])

        if (issueList.length > 0) {
          member.error = issueList.map((item: any) => item.error).join(' | ')
          member.note = _.uniq(issueList.map((item: any) => item.recommended_fix).concat(warningList)).join(' ')
          dataGroups[3].items.push(member)
          total++
          return
        }

        const isUpdated = !!existing && (
          (
            !member._profile_only && (
              existing.first_name !== member.first_name ||
              existing.last_name !== member.last_name ||
              existing.team_name !== member.team_name ||
              existing.direct_manager_email !== member.direct_manager_email ||
              existing.language !== member.language ||
              existing.country !== member.country ||
              existing.time_zone !== member.time_zone ||
              existing.user_type !== member.user_type
            )
          ) ||
          (member._optional_fields.nickname && existing.nickname !== member.nickname) ||
          (member._optional_fields.birthday && existing.birthday !== member.birthday) ||
          (member._optional_fields.workdate && existing.workdate !== member.workdate)
        )

        member.note = warningList.length > 0 ? _.uniq(warningList).join(' ') : 'Ready to import'

        if (existing) {
          if (!isUpdated) {
            return
          }
          dataGroups[1].items.push(member)
        } else {
          dataGroups[0].items.push(member)
        }
        total++
      })

      commit('setImportMembersTableDataGroupsTotal', total)
      commit('setImportMembersTableDataGroups', dataGroups)

      commit('setOrigImportMembersTableDataGroupsTotal', total)
      commit('setOrigImportMembersTableDataGroups', _.cloneDeep(dataGroups))
    },
    async legacyImportMembersResolve({commit, dispatch, state,rootState}:any){
      const teamMaster = await getTeamByCompany(rootState.authen.user.company)//(await firebase.database().ref('team').orderByChild("company").equalTo(`${rootState.authen.user.company}`).once('value')).val();
      const allUser = await getUserAll(rootState.authen.user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val();

      const itemToFormObject = (item:any) => {
        return {
          "pic": "",
          "firstname": item.first_name,
          "lastname": item.last_name,
          "email": item.email,
          "dispteam": item.team_name,
          "team_color": "F0DC05",
          "dispboss": item.manager_name,
          "team": "", /* TODO: get from team */
          "boss": item.direct_manager_email,
          "tmp_boss": "",
          "boss_pic": "",
          "boss_firstname": "",
          "boss_lastname": "",
          "language": item.language,
          "country": item.country,
          "time_zone": "+07:00", /* TODO: get from company */
          "user_type": "user",
          "status": "new",
          "to_now": "",
          "streak_count": 0,
          "super_feedback_count": 0,
          "medal": [],
          "coin": 0,
          "gem": 0,
          "house": "",
          "joined": "",
          "joined_tooltips": [],
          "dispmanager": "",
          "introduce": "",
          "culture_add_percentile": "",
          "large_pic": "",
          "boss_team_name": "" /* TODO: get from team */
        }
      }

      const createMember = async (obj:any) => {
        let form = obj.form;
        let invite = obj.invite;

        let teamFind = _.findKey(teamMaster, item => {
          return item.name.toLowerCase() === form.dispteam.toLowerCase();
        });
        let bossKey = form.boss.replace(/\./g, "||");
        let parentId = (allUser && allUser[bossKey] && allUser[bossKey].team)?allUser[bossKey].team : "";

        // console.log(teamFind, "teamFind");
        // console.log(bossKey, "bossKey");
        // console.log(parentId, "parentId");

        let team:any = "";
        if(teamFind && teamFind !== undefined){
          team = teamFind
          // console.log(rootState.newmember.teamByKey, "state.teamByKey");
          // if(state.teamByKey[team] && parentId && team !== parentId){
          if (parentId && team !== parentId) {
            firebase.database().ref(`team/${team}`).update({
              parent:parentId,
              updated:format(new Date(), 'YYYY-MM-DD HH:mm')
            }).then(()=>{
              firebase.database().ref(`/genorganize`).push({
                  company:rootState.authen.user.company
              });
            });
          }
        } else {
          let internal_id = "t"+globalfunc.generateRandomNumber()
          team = firebase.database().ref('team').push().key;
          // console.log(internal_id, "internal_id");
          const newTeam = {
            name: form.dispteam,
            parent: parentId,
            company: rootState.authen.user.company,
            internal_id:internal_id,
            updated:format(new Date(), 'YYYY-MM-DD HH:mm')
          };
          // console.log(newTeam, "newTeam");
          teamMaster[team] = newTeam;
          firebase.database().ref(`team/${team}`).update(newTeam).then(()=>{
            firebase.database().ref(`/genorganize`).push({
                company:rootState.authen.user.company
            });
            /*save logs*/
            dispatch('authen/addLogs', {
              company: rootState.authen.user.company,
                createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                id: team,
                table: 'team',
                status: 'add',
                user: rootState.authen.user.email
            }, {root:true})
          });
        }

        let ctemail = form.email.toLowerCase()
        ctemail = ctemail.replace(/ /g, "")
        const newId = ctemail.replace(/\./g, "||")
        form.time_zone=Number(form.time_zone.replace(':','.'))
        let time_zone=(+form.time_zone == Math.floor(+form.time_zone))?parseInt(form.time_zone):parseFloat(form.time_zone.toFixed(2))
        const userComp = rootState.authen.user.company
        const userBoss = form.boss

        const nowDate = format(new Date(), 'YYYY-MM-DD HH:mm');
        const userData = {
          firstname:form.firstname.trim(),
          lastname:form.lastname.trim(),
          email:ctemail,
          company:userComp,
          team,
          boss:userBoss,
          language:form.language,
          user_type:form.user_type,
          gem:0,
          coin:0,
          powerup:{
            fivecoins:"14",
            threecoins:"7",
          },
          profilepic:"",
          address:"",
          mobile:"",
          phone:"",
          timezone:time_zone,
          country:form.country.toLowerCase().trim()==="thailand"?'TH':form.country,
          updated: nowDate,
          createdatetime: nowDate,
          modifydatetime: nowDate
        }

        firebase.database().ref(`/users`).child(newId).set(userData)
        .then(()=>{
          if(userBoss){
            firebase.database().ref(`/genboss`).push({
              company:userComp
            })
          }
        })
        .then(()=>{
          if(invite == true){
            firebase.database().ref(`/user_invite`).push({
              email:ctemail
            }).then(()=>{
              let Id = ctemail.replace(/\./g, "||")
              firebase.database().ref(`/users/${Id}`).update({
                invited:format(new Date(), 'YYYY-MM-DD HH:mm:ss')
              })
            })
          }
        }).then(()=>{
          dispatch('authen/addLogs', {
            company: userComp,
            createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            id: newId,
            table: 'users',
            status: 'add',
            user: rootState.authen.user.email
          }, {root:true});
        });
      }

      const updateMember = async (value:any) => {
        const id = value.form.email.replace(/\./g, "||");
        // console.log(value, "value");
        // return;
        const boss = value.form.boss;
        const userComp = rootState.authen.user.company;
        let team:any = "";

        let teamFind = _.findKey(teamMaster, item => {
          return item.name.toLowerCase() === value.form.dispteam.toLowerCase();
        });
        
        let bossKey = boss.replace(/\./g, "||");
        let parentId = (allUser[bossKey] && allUser[bossKey].team)?allUser[bossKey].team : "";

        if(teamFind && teamFind !== undefined){
          team = teamFind
          if(parentId && team !== parentId){
            firebase.database().ref(`team/${team}`).update({
              parent:parentId,
              updated:format(new Date(), 'YYYY-MM-DD HH:mm')
            }).then(()=>{
              firebase.database().ref(`/genorganize`).push({
                  company:rootState.authen.user.company
              });
            });
          }
        } else {
          let internal_id = "t"+globalfunc.generateRandomNumber()
          team = firebase.database().ref('team').push().key;
          firebase.database().ref(`team/${team}`).update({
            name:value.form.dispteam,
            parent:parentId,
            company:rootState.authen.user.company,
            internal_id: internal_id,
            updated:format(new Date(), 'YYYY-MM-DD HH:mm')
          }).then(()=>{
            firebase.database().ref(`/genorganize`).push({
                company:rootState.authen.user.company
            });
            /*save logs*/
            dispatch('authen/addLogs', {
              company: rootState.authen.user.company,
                createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                id: team,
                table: 'team',
                status: 'add',
                user: rootState.authen.user.email
            }, {root:true})
          });
        }

        value.form.time_zone=Number(value.form.time_zone.replace(':','.'));
        let time_zone=(+value.form.time_zone == Math.floor(+value.form.time_zone))?parseInt(value.form.time_zone):parseFloat(value.form.time_zone.toFixed(2));
        const userData = {
          firstname:value.form.firstname.trim(),
          lastname:value.form.lastname.trim(),
          team:team,
          boss:boss,
          language:value.form.language,
          user_type:value.form.user_type,
          timezone:time_zone,
          country:value.form.country.toLowerCase().trim()==="thailand"?'TH':value.form.country,
          // updated:format(new Date(), 'YYYY-MM-DD HH:mm'),
          modifydatetime:format(new Date(), 'YYYY-MM-DD HH:mm')
        }
        firebase.database().ref(`/users/${id}`).update(userData).then(()=>{
            firebase.database().ref(`/genboss`).push({
              company:userComp
            })
        }).then(()=>{
            
            dispatch('authen/addLogs', {
              company: userComp,
              createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              // id: value.id,
              id: id,
              table: 'users',
              status: 'edit',
              user: rootState.authen.user.email
            }, {root:true}).then(function() {
            });
          });
      }

      for (const group of state.importMembersTableDataGroups) {
        // console.log(group.code, "group.code");
        for (const item of group.items) {
          // console.log(item, "item");
          if (group.code == "new" || group.code == "resolve") {
            // console.log("new");
            // @todo create new member
            // check This email has already been registered.
            // check The email is already in use (as an archived member) 
            await createMember({
              form: itemToFormObject(item),
              invite: false,
            });
          } else if (group.code == "update") {
            await updateMember({
              form: itemToFormObject(item)
            });
          }
        }
      }

    },
    async importMembersResolve({dispatch, state,rootState}:any){
      const unresolvedGroup = _.find(state.importMembersTableDataGroups, { code: 'resolve' })
      if (unresolvedGroup && unresolvedGroup.items.length > 0) {
        throw new Error('Resolve all import issues before confirming the import.')
      }

      const actionableItems = _.flatMap(
        state.importMembersTableDataGroups.filter((group: any) => ['new', 'update'].indexOf(group.code) !== -1),
        'items'
      )

      if (actionableItems.length === 0) {
        return {
          backupId: null,
          importedCount: 0,
        }
      }

      const companyId = rootState.authen.user.company
      const actorEmail = rootState.authen.user.email
      const teamMaster = _.cloneDeep(await getTeamByCompany(companyId) || {})
      const allUser = _.cloneDeep(await getUserAll(companyId) || {})
      const nowTimestamp = format(new Date(), 'YYYY-MM-DD HH:mm:ss')
      const backupRef = firebase.database().ref(`company/${companyId}/import_member_backups`).push()
      const backupId = backupRef.key
      const backupPath = `company/${companyId}/import_member_backups/${backupId}`
      const getImportErrorMessage = (error: any) => {
        return normalizeImportString(error && error.message ? error.message : error)
      }
      const isPermissionDeniedError = (error: any) => {
        const message = getImportErrorMessage(error).toLowerCase()
        return message.indexOf('permission denied') !== -1 || message.indexOf('permission_denied') !== -1
      }

      const impactedUserKeys = _.uniq(
        actionableItems
          .map((item: any) => normalizeImportEmail(item.email))
          .filter(Boolean)
          .map((email: string) => email.replace(/\./g, '||'))
      )
      const usersBefore: Record<string, any> = {}
      impactedUserKeys.forEach((key) => {
        usersBefore[key] = _.has(allUser, key) ? _.cloneDeep(allUser[key]) : null
      })

      try {
        await backupRef.set({
          company: companyId,
          created_at: nowTimestamp,
          created_by: actorEmail,
          status: 'pending',
          items: _.cloneDeep(actionableItems),
          users_before: usersBefore,
          teams_before: _.cloneDeep(teamMaster),
        })
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          throw new Error(`Permission denied while saving the pre-import backup at ${backupPath}.`)
        }
        throw error
      }

      const actionableByEmail = new Map()
      actionableItems.forEach((item: any) => {
        actionableByEmail.set(normalizeImportEmail(item.email), item)
      })

      const depthCache = new Map()
      const getDepth = (email: string, visiting = new Set<string>()) => {
        if (depthCache.has(email)) {
          return depthCache.get(email)
        }
        if (visiting.has(email)) {
          return 0
        }
        visiting.add(email)
        const item = actionableByEmail.get(email)
        if (!item || !item.direct_manager_email || !actionableByEmail.has(item.direct_manager_email)) {
          depthCache.set(email, 0)
          visiting.delete(email)
          return 0
        }
        const depth = getDepth(item.direct_manager_email, visiting) + 1
        depthCache.set(email, depth)
        visiting.delete(email)
        return depth
      }

      const orderedItems = _.orderBy(
        actionableItems,
        [
          (item: any) => getDepth(normalizeImportEmail(item.email)),
          (item: any) => _.get(item, 'row', 0),
        ],
        ['asc', 'asc']
      )

      const createdUsers: string[] = []
      const updatedUsers: string[] = []
      const createdTeams: string[] = []
      const touchedTeamIds = new Set<string>()
      let needsBossRebuild = false
      let needsOrgRebuild = false

      const findTeamIdByName = (teamName: string) => {
        return _.findKey(teamMaster, (team: any) => {
          return normalizeImportString(team.name).toLowerCase() === normalizeImportString(teamName).toLowerCase()
        })
      }

      const ensureTeamForMember = async (item: any) => {
        const teamName = normalizeImportString(item.team_name)
        let teamId = findTeamIdByName(teamName)
        const managerKey = normalizeImportEmail(item.direct_manager_email).replace(/\./g, '||')
        const parentId = managerKey && _.get(allUser, `${managerKey}.team`)
          ? _.get(allUser, `${managerKey}.team`)
          : ''
        const nowDate = format(new Date(), 'YYYY-MM-DD HH:mm')

        if (teamId) {
          const currentTeam = teamMaster[teamId] || {}
          if (_.get(currentTeam, 'parent', '') !== parentId) {
            await firebase.database().ref(`team/${teamId}`).update({
              parent: parentId,
              updated: nowDate,
            })
            teamMaster[teamId] = {
              ...currentTeam,
              parent: parentId,
              updated: nowDate,
            }
            needsOrgRebuild = true
          }
        } else {
          teamId = firebase.database().ref('team').push().key
          const newTeam = {
            name: teamName,
            parent: parentId,
            company: companyId,
            internal_id: `t${globalfunc.generateRandomNumber()}`,
            updated: nowDate,
          }
          await firebase.database().ref(`team/${teamId}`).set(newTeam)
          teamMaster[teamId] = newTeam
          createdTeams.push(teamId)
          needsOrgRebuild = true
          await dispatch('authen/addLogs', {
            company: companyId,
            createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            id: teamId,
            table: 'team',
            status: 'add',
            user: actorEmail,
          }, {root:true})
        }

        touchedTeamIds.add(teamId)
        return teamId
      }

      const applyOptionalFields = (target: any, item: any, isNew: boolean) => {
        if (isNew || _.get(item, '_optional_fields.nickname', false)) {
          target.nickname = normalizeImportString(item.nickname)
        }
        if (isNew || _.get(item, '_optional_fields.birthday', false)) {
          target.birthday = normalizeStoredBirthday(item.birthday)
        }
        if (isNew || _.get(item, '_optional_fields.workdate', false)) {
          target.workdate = normalizeStoredWorkdate(item.workdate)
        }
      }

      try {
        for (const item of orderedItems) {
          const email = normalizeImportEmail(item.email)
          const userKey = email.replace(/\./g, '||')
          const nowDate = format(new Date(), 'YYYY-MM-DD HH:mm')
          const existingUser = _.get(allUser, userKey, null)

          if (_.get(item, '_profile_only', false)) {
            if (!existingUser) {
              throw new Error(`Cannot apply profile-only update for ${email}. The member does not exist.`)
            }

            const profileUpdatePayload: any = {
              modifydatetime: nowDate,
            }
            applyOptionalFields(profileUpdatePayload, item, false)

            await firebase.database().ref(`/users/${userKey}`).update(profileUpdatePayload)
            allUser[userKey] = {
              ...existingUser,
              ...profileUpdatePayload,
            }
            updatedUsers.push(userKey)

            await dispatch('authen/addLogs', {
              company: companyId,
              createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              id: userKey,
              table: 'users',
              status: 'edit',
              user: actorEmail
            }, {root:true})
            continue
          }

          const teamId = await ensureTeamForMember(item)
          const timezoneValue = offsetStringToNumber(item.time_zone)

          if (!existingUser) {
            const newUserData: any = {
              firstname: normalizeImportString(item.first_name),
              lastname: normalizeImportString(item.last_name),
              email,
              company: companyId,
              team: teamId,
              boss: normalizeImportEmail(item.direct_manager_email),
              language: item.language,
              user_type: item.user_type || 'user',
              gem: 0,
              coin: 0,
              powerup: {
                fivecoins: '14',
                threecoins: '7',
              },
              profilepic: '',
              address: '',
              mobile: '',
              phone: '',
              timezone: timezoneValue,
              country: normalizeImportCountry(item.country, 'TH'),
              updated: nowDate,
              createdatetime: nowDate,
              modifydatetime: nowDate,
            }
            applyOptionalFields(newUserData, item, true)

            await firebase.database().ref(`/users/${userKey}`).set(newUserData)
            allUser[userKey] = {
              ...newUserData,
              team_name: item.team_name,
            }
            createdUsers.push(userKey)
            needsBossRebuild = true

            await dispatch('authen/addLogs', {
              company: companyId,
              createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              id: userKey,
              table: 'users',
              status: 'add',
              user: actorEmail
            }, {root:true})
          } else {
            const updatePayload: any = {
              firstname: normalizeImportString(item.first_name),
              lastname: normalizeImportString(item.last_name),
              team: teamId,
              boss: normalizeImportEmail(item.direct_manager_email),
              language: item.language,
              user_type: item.user_type || existingUser.user_type || 'user',
              timezone: timezoneValue,
              country: normalizeImportCountry(item.country, existingUser.country || 'TH'),
              modifydatetime: nowDate,
            }
            applyOptionalFields(updatePayload, item, false)

            await firebase.database().ref(`/users/${userKey}`).update(updatePayload)
            allUser[userKey] = {
              ...existingUser,
              ...updatePayload,
              email,
              team_name: item.team_name,
            }
            updatedUsers.push(userKey)
            needsBossRebuild = true

            await dispatch('authen/addLogs', {
              company: companyId,
              createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              id: userKey,
              table: 'users',
              status: 'edit',
              user: actorEmail
            }, {root:true})
          }
        }

        if (needsBossRebuild) {
          await firebase.database().ref('/genboss').push({
            company: companyId
          })
        }
        if (needsOrgRebuild) {
          await firebase.database().ref('/genorganize').push({
            company: companyId
          })
        }

        await backupRef.update({
          status: 'completed',
          completed_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          created_users: createdUsers,
          updated_users: updatedUsers,
          created_teams: createdTeams,
          touched_team_ids: Array.from(touchedTeamIds),
        })

        return {
          backupId,
          importedCount: orderedItems.length,
        }
      } catch (error) {
        try {
          await backupRef.update({
            status: 'failed',
            failed_at: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
            error_message: getImportErrorMessage(error),
          })
        } catch (backupError) {
          // Ignore secondary backup status failures so the original import error is preserved.
        }
        throw error
      }
    },
    async importMembersSearch({commit, dispatch, state, rootState}:any, value:any){
      commit('setImportMembersSearch', value);

      const dataGroups: any[] = [];
      let total = 0;
      const keyword = normalizeImportString(value).toLowerCase();
      for (const i in state.origImportMembersTableDataGroups) {
        const group = state.origImportMembersTableDataGroups[i];
        const newItems: any[] = [];
        for (const item of group.items) {
          if (!keyword) {
            newItems.push(item);
          } else if (normalizeImportString(item.email).toLowerCase().includes(keyword)) {
            newItems.push(item);
          } else if (normalizeImportString(item.team_name).toLowerCase().includes(keyword)) {
            newItems.push(item);
          } else if (normalizeImportString(item.manager_name).toLowerCase().includes(keyword)) {
            newItems.push(item);
          } else if (buildImportName(item.first_name, item.last_name).toLowerCase().includes(keyword)) {
            newItems.push(item);
          } else if (normalizeImportString(item.nickname).toLowerCase().includes(keyword)) {
            newItems.push(item);
          }
        }
        total += newItems.length;
        dataGroups.push({
          code: group.code || "",
          name: group.name || "",
          items: newItems
        });
      }

      commit('setImportMembersTableDataGroupsTotal', total);
      commit('setImportMembersTableDataGroups', dataGroups);
    },
  },
  getters: {
    ssoEnabled: (state: any) => {
      return state.ssoEnabled
    },
    selectedSso: (state: any) => {
      return state.selectedSso
    },

    apiKeys: (state: any) => {
      return state.apiKeys
    },
    apiKeyDev: (state: any) => {
      return state.apiKeyDev
    },
    apiKeyProd: (state: any) => {
      return state.apiKeyProd
    },
    apiLogs: (state: any) => {
      return state.apiLogs
    },
    dataSettings: (state: any) => {
      return state.dataSettings
    },
    hasDirectorySyncSettings: (state: any) => {
      return state.hasDirectorySyncSettings
    },
    directorySyncSettings: (state: any) => {
      return state.directorySyncSettings
    },
    resultsSimulate: (state: any) => {
      return state.resultsSimulate
    },
    membersSimulate: (state: any) => {
      return state.membersSimulate
    },
    membersSimulateAll: (state: any) => {
      return state.membersSimulateAll
    },
    pagesimulate: (state: any) => {
      return state.pagesimulate
    },
    inputSearchSimulate: (state: any) => {
      return state.inputSearchSimulate
    },
    loadingSimulateTable: (state: any) => {
      return state.loadingSimulateTable
    },
    resultsView: (state: any) => {
      return state.resultsView
    },
    membersView: (state: any) => {
      return state.membersView
    },
    membersViewAll: (state: any) => {
      return state.membersViewAll
    },
    pageview: (state: any) => {
      return state.pageview
    },
    inputSearchView: (state: any) => {
      return state.inputSearchView
    },
    loadingViewTable: (state: any) => {
      return state.loadingViewTable
    },
    reviewUpdates: (state: any) => {
      return state.reviewUpdates
    },
    reviewUpdatesAll: (state: any) => {
      return state.reviewUpdatesAll
    },
    reviewUpdatesLoading: (state: any) => {
      return state.reviewUpdatesLoading
    },
    reviewUpdatesTotal: (state: any) => {
      return state.reviewUpdatesTotal
    },
    reviewUpdatesPage: (state: any) => {
      return state.reviewUpdatesPage
    },
    reviewUpdatesByKey: (state: any) => {
      return state.reviewUpdatesByKey
    },
    pageReviewUpdates: (state: any) => {
      return state.pageReviewUpdates
    },
    reviewUpdatesIgnorePendingStatus: (state: any) => {
      return state.pageReviewUpdates
    },
    importMembersTableDataGroups: (state: any) => {
      return state.importMembersTableDataGroups
    },
    importMembersTableDataGroupsTotal: (state: any) => {
      return state.importMembersTableDataGroupsTotal
    }
  }
}
